// practice-library-resources/data/realLibraryData.js
// Real InterviewGenieAI practice-library data.
// Frontend fallback + optional merge layer for /api/user/library/feed.
//
// YouTube videos use public YouTube embed IDs. Questions are original,
// role-specific interview prompts designed for AI interview practice.

const yt = (id) => `https://img.youtube.com/vi/${id}/hqdefault.jpg`;

const qs = {
  fullstack: [
    {
      id: "fs-q1",
      question: "Walk me through how you would design authentication for a React + Flask app.",
      type: "technical",
      difficulty: "Intermediate",
      role: "Full Stack Developer",
      evaluationFocus: "Security, sessions/JWT, refresh tokens, route protection, error handling.",
      answerFramework: "Clarify requirements → choose session/JWT strategy → explain backend validation → protect frontend routes → cover logout/expiry."
    },
    {
      id: "fs-q2",
      question: "A user submits a form but the dashboard does not update. How would you debug it end to end?",
      type: "debugging",
      difficulty: "Intermediate",
      role: "Full Stack Developer",
      evaluationFocus: "Browser devtools, network calls, API response, state management, database verification.",
      answerFramework: "Reproduce → inspect frontend state → inspect network request → check backend logs → verify DB write → add regression test."
    },
    {
      id: "fs-q3",
      question: "How would you structure API pagination, filtering, and search for a resource library?",
      type: "system design",
      difficulty: "Intermediate",
      role: "Full Stack Developer",
      evaluationFocus: "Query parameters, indexing, UX latency, caching, response schema.",
      answerFramework: "Define schema → query parameters → DB indexes → API response shape → debounce/search UX → cache strategy."
    },
    {
      id: "fs-q4",
      question: "Tell me about a time you improved a slow page or API endpoint.",
      type: "behavioral",
      difficulty: "Intermediate",
      role: "Full Stack Developer",
      evaluationFocus: "STAR structure, measurement, optimization choices, result metrics.",
      answerFramework: "Situation → bottleneck measurement → action taken → measurable result → what you learned."
    }
  ],
  frontend: [
    {
      id: "fe-q1",
      question: "Explain the difference between controlled and uncontrolled components in React.",
      type: "technical",
      difficulty: "Beginner",
      role: "Frontend Developer",
      evaluationFocus: "React state, form handling, refs, tradeoffs.",
      answerFramework: "Define both → show when to use each → mention validation and performance implications."
    },
    {
      id: "fe-q2",
      question: "How would you avoid unnecessary re-renders in a large dashboard?",
      type: "technical",
      difficulty: "Intermediate",
      role: "Frontend Developer",
      evaluationFocus: "Memoization, component boundaries, selectors, virtualization.",
      answerFramework: "Measure first → isolate state → memo/useMemo/useCallback → virtualize lists → avoid premature optimization."
    },
    {
      id: "fe-q3",
      question: "A modal works on desktop but is broken on mobile. What would you check?",
      type: "debugging",
      difficulty: "Intermediate",
      role: "Frontend Developer",
      evaluationFocus: "Responsive layout, z-index, overflow, accessibility, browser testing.",
      answerFramework: "Reproduce on device → inspect layout/z-index → keyboard/focus handling → safe-area/viewport → fix with tests."
    },
    {
      id: "fe-q4",
      question: "How do you make a React page accessible for keyboard and screen-reader users?",
      type: "technical",
      difficulty: "Intermediate",
      role: "Frontend Developer",
      evaluationFocus: "Semantic HTML, ARIA, focus management, contrast, testing.",
      answerFramework: "Use semantic elements → labels/roles only when needed → focus states → keyboard paths → test with screen readers."
    }
  ],
  backend: [
    {
      id: "be-q1",
      question: "How would you design a REST endpoint that creates an interview session safely?",
      type: "technical",
      difficulty: "Intermediate",
      role: "Backend Developer",
      evaluationFocus: "Validation, auth, transactions, idempotency, status codes.",
      answerFramework: "Validate input → authorize user → transaction → create session/questions → return clean response → log errors."
    },
    {
      id: "be-q2",
      question: "What database indexes would you add for a library with search, category, role, and difficulty filters?",
      type: "technical",
      difficulty: "Intermediate",
      role: "Backend Developer",
      evaluationFocus: "Composite indexes, selectivity, text search, query plans.",
      answerFramework: "Identify query patterns → index filters → handle full-text search → explain tradeoffs → verify with EXPLAIN."
    },
    {
      id: "be-q3",
      question: "How would you handle rate limiting and abuse prevention for AI-generated interviews?",
      type: "system design",
      difficulty: "Advanced",
      role: "Backend Developer",
      evaluationFocus: "Rate limits, quotas, auth, plan limits, Redis, logging.",
      answerFramework: "User/IP limits → plan quotas → Redis counters → graceful errors → admin monitoring."
    },
    {
      id: "be-q4",
      question: "A production API returns 500 only for paid users. How do you debug it?",
      type: "debugging",
      difficulty: "Advanced",
      role: "Backend Developer",
      evaluationFocus: "Logs, feature flags, database state, subscription logic, safe rollback.",
      answerFramework: "Check logs → isolate paid-user path → compare DB rows → reproduce with test account → patch + migration if needed."
    }
  ],
  data: [
    {
      id: "da-q1",
      question: "Write a SQL query to find users who practiced this week but did not complete an interview.",
      type: "technical",
      difficulty: "Intermediate",
      role: "Data Analyst",
      evaluationFocus: "Joins, date filters, null checks, aggregation.",
      answerFramework: "Define tables → filter date range → left join completions → where completion is null → validate counts."
    },
    {
      id: "da-q2",
      question: "A conversion dashboard dropped by 30% overnight. How would you investigate?",
      type: "case",
      difficulty: "Intermediate",
      role: "Data Analyst",
      evaluationFocus: "Segmentation, event tracking, data quality, release correlation.",
      answerFramework: "Confirm data quality → segment by channel/device/plan → compare releases → identify funnel step → propose next action."
    },
    {
      id: "da-q3",
      question: "How would you measure whether AI interview feedback improves user retention?",
      type: "case",
      difficulty: "Advanced",
      role: "Data Analyst",
      evaluationFocus: "Cohorts, experiments, retention metrics, bias.",
      answerFramework: "Define retention → build cohorts → A/B test if possible → control confounders → report confidence and business impact."
    },
    {
      id: "da-q4",
      question: "Write a SQL query to find the second-highest interview score per user.",
      type: "technical",
      difficulty: "Intermediate",
      role: "Data Analyst",
      evaluationFocus: "Window functions, edge cases (ties), readability.",
      answerFramework: "Use ROW_NUMBER or DENSE_RANK over PARTITION BY user → filter rank=2 → handle ties explicitly."
    },
    {
      id: "da-q5",
      question: "Explain p-values and confidence intervals to a non-technical PM.",
      type: "communication",
      difficulty: "Intermediate",
      role: "Data Analyst",
      evaluationFocus: "Plain language, real example, common pitfalls.",
      answerFramework: "Frame the question → use a real example → say what each does and doesn't say → call out pitfalls."
    },
    {
      id: "da-q6",
      question: "An A/B test shows +0.5% conversion with p = 0.03. Should we ship?",
      type: "experimentation",
      difficulty: "Advanced",
      role: "Data Analyst",
      evaluationFocus: "Power, peeking, business significance, downstream metrics.",
      answerFramework: "Check power and sample → confirm not peeking → business impact at scale → guardrail metrics → decision."
    },
    {
      id: "da-q7",
      question: "Tell me about a dashboard nobody used. Why?",
      type: "behavioral",
      difficulty: "Intermediate",
      role: "Data Analyst",
      evaluationFocus: "Stakeholder alignment, metric tree, iteration.",
      answerFramework: "Context → assumed need → actual need → redesign → adoption metric."
    },
    {
      id: "da-q8",
      question: "Design a metric tree for a freemium SaaS interview product.",
      type: "case",
      difficulty: "Advanced",
      role: "Data Analyst",
      evaluationFocus: "North star, leading indicators, trade-offs, traps.",
      answerFramework: "Pick North Star → break into AARM → leading indicators → call out vanity metrics."
    }
  ],
  pm: [
    {
      id: "pm-q1",
      question: "Design a feature that helps job seekers choose the right interview type.",
      type: "product case",
      difficulty: "Intermediate",
      role: "Product Manager",
      evaluationFocus: "User problem, prioritization, UX, metrics.",
      answerFramework: "Clarify user/persona → identify pain → propose feature → prioritize MVP → define success metrics."
    },
    {
      id: "pm-q2",
      question: "How would you decide whether to build video interviews now or improve voice interviews first?",
      type: "prioritization",
      difficulty: "Advanced",
      role: "Product Manager",
      evaluationFocus: "Impact, effort, risk, evidence, roadmap tradeoffs.",
      answerFramework: "Clarify goal → compare user value → estimate effort/risk → use data → decide and define next experiment."
    },
    {
      id: "pm-q3",
      question: "Tell me about a time you changed direction after learning something from users.",
      type: "behavioral",
      difficulty: "Intermediate",
      role: "Product Manager",
      evaluationFocus: "Customer empathy, decision-making, communication.",
      answerFramework: "Situation → user signal → tradeoff → action → measurable result."
    },
    {
      id: "pm-q4",
      question: "Estimate how many AI mock interviews are run globally each day.",
      type: "estimation",
      difficulty: "Intermediate",
      role: "Product Manager",
      evaluationFocus: "Top-down structure, sanity checks, assumptions, segmentation.",
      answerFramework: "Define population → assumption per segment → arithmetic → sanity check → adjust."
    },
    {
      id: "pm-q5",
      question: "Daily active users dropped 6% over the weekend. Walk me through what you do Monday morning.",
      type: "execution",
      difficulty: "Advanced",
      role: "Product Manager",
      evaluationFocus: "Triage, hypothesis-driven debugging, communication, prioritization.",
      answerFramework: "Confirm metric → segment by platform/region → identify likely cause → hypothesize → validate → action plan."
    },
    {
      id: "pm-q6",
      question: "Improve the YouTube recommendation feed for non-English-speaking users.",
      type: "product case",
      difficulty: "Advanced",
      role: "Product Manager",
      evaluationFocus: "User insight, market awareness, MVP scoping, success metrics.",
      answerFramework: "Clarify user → pain points → 3 ideas → pick highest impact → metrics → risks."
    },
    {
      id: "pm-q7",
      question: "How do you say no to engineering work that's already half-built?",
      type: "behavioral",
      difficulty: "Advanced",
      role: "Product Manager",
      evaluationFocus: "Stakeholder management, evidence, sunk cost reasoning, communication.",
      answerFramework: "Context → fresh evidence → reframe sunk cost → decision + alternatives → outcome."
    },
    {
      id: "pm-q8",
      question: "How do you measure success for a feature that improves trust but not engagement?",
      type: "product",
      difficulty: "Advanced",
      role: "Product Manager",
      evaluationFocus: "Metric tree, qualitative + quantitative, leading indicators.",
      answerFramework: "Define trust → leading indicators → qualitative signals → tradeoff with engagement → reporting cadence."
    },
    {
      id: "pm-q9",
      question: "Design a freemium-to-paid conversion flow that doesn't feel pushy.",
      type: "product case",
      difficulty: "Advanced",
      role: "Product Manager",
      evaluationFocus: "User psychology, value moments, friction tradeoffs, metrics.",
      answerFramework: "Identify value moment → soft surface → quantify lift → A/B test → iterate."
    }
  ],
  devops: [
    {
      id: "do-q1",
      question: "A Render deployment passes build but crashes at runtime. What do you check first?",
      type: "debugging",
      difficulty: "Intermediate",
      role: "DevOps Engineer",
      evaluationFocus: "Env vars, start command, logs, migrations, health checks.",
      answerFramework: "Read runtime logs → verify env vars → check start command → validate DB/migrations → add health endpoint."
    },
    {
      id: "do-q2",
      question: "How would you design CI/CD for a Flask + React SaaS with MySQL and Redis?",
      type: "system design",
      difficulty: "Advanced",
      role: "DevOps Engineer",
      evaluationFocus: "Build stages, tests, migrations, secrets, rollback.",
      answerFramework: "Separate frontend/backend pipelines → tests/lint → migrations → secret handling → deploy + rollback."
    },
    {
      id: "do-q3",
      question: "How do you monitor background workers and failed AI interview jobs?",
      type: "technical",
      difficulty: "Intermediate",
      role: "DevOps Engineer",
      evaluationFocus: "Queues, retries, dead-letter patterns, alerts, dashboards.",
      answerFramework: "Track queue depth → failures/retries → structured logs → alert thresholds → replay safely."
    },
    {
      id: "do-q4",
      question: "How would you set up zero-downtime database migrations on a large production table?",
      type: "system design",
      difficulty: "Advanced",
      role: "DevOps Engineer",
      evaluationFocus: "Schema versioning, dual writes, backfill, lock avoidance, rollback.",
      answerFramework: "Backward-compatible schema → dual-write → backfill async → cut over → cleanup."
    },
    {
      id: "do-q5",
      question: "Walk through a recent production incident you led.",
      type: "behavioral",
      difficulty: "Advanced",
      role: "DevOps Engineer",
      evaluationFocus: "Detection, communication, mitigation, root cause, follow-up.",
      answerFramework: "Detection → containment → comms cadence → mitigation → root cause → action items."
    },
    {
      id: "do-q6",
      question: "How do you keep CI fast for a fast-growing monorepo?",
      type: "process",
      difficulty: "Advanced",
      role: "DevOps Engineer",
      evaluationFocus: "Caching, affected-graph builds, parallelization, flake reduction.",
      answerFramework: "Affected-graph build → caching strategy → parallel test shards → flake budget → measure CI median + p95."
    },
    {
      id: "do-q7",
      question: "How would you design backups and disaster recovery for a multi-tenant SaaS?",
      type: "system design",
      difficulty: "Advanced",
      role: "DevOps Engineer",
      evaluationFocus: "RTO/RPO, isolation, restore drills, encryption, compliance.",
      answerFramework: "Define RTO/RPO → snapshot strategy → cross-region copy → restore drills → encryption + audit."
    },
    {
      id: "do-q8",
      question: "Explain the difference between containers and virtual machines.",
      type: "technical",
      difficulty: "Beginner",
      role: "DevOps Engineer",
      evaluationFocus: "Isolation, kernel boundary, density, startup time.",
      answerFramework: "Define each → kernel/userland boundary → density tradeoff → typical use cases → caveats."
    }
  ],
  qa: [
    {
      id: "qa-q1",
      question: "How would you test an interview flow from signup to final analytics?",
      type: "testing",
      difficulty: "Intermediate",
      role: "QA Engineer",
      evaluationFocus: "E2E coverage, edge cases, test data, acceptance criteria.",
      answerFramework: "Map critical path → define test data → automate happy path → add failure cases → verify analytics output."
    },
    {
      id: "qa-q2",
      question: "What tests would you add after fixing JavaScript/TypeScript editor compilation?",
      type: "testing",
      difficulty: "Intermediate",
      role: "QA Engineer",
      evaluationFocus: "Regression tests, language modes, API contracts, UI state.",
      answerFramework: "Unit parse tests → integration compile route tests → UI language switch tests → error message tests."
    },
    {
      id: "qa-q3",
      question: "A bug only happens in production for one subscription plan. How do you reproduce it safely?",
      type: "debugging",
      difficulty: "Advanced",
      role: "QA Engineer",
      evaluationFocus: "Environment parity, test accounts, logs, non-destructive reproduction.",
      answerFramework: "Create equivalent test user → mirror plan state → inspect logs → reproduce in staging → document exact steps."
    },
    {
      id: "qa-q4",
      question: "How do you decide what to automate vs leave manual?",
      type: "process",
      difficulty: "Intermediate",
      role: "QA Engineer",
      evaluationFocus: "ROI, flake risk, change frequency, risk severity.",
      answerFramework: "List candidates → score by frequency × cost-of-bug × stability → automate top tier → revisit quarterly."
    },
    {
      id: "qa-q5",
      question: "Walk me through your test pyramid for a SaaS web app.",
      type: "system design",
      difficulty: "Intermediate",
      role: "QA Engineer",
      evaluationFocus: "Layers, ratio, ownership, speed.",
      answerFramework: "Unit → integration → contract → end-to-end → smoke. Define ratios and ownership at each layer."
    },
    {
      id: "qa-q6",
      question: "Tell me about a time you caught a bug nobody else saw.",
      type: "behavioral",
      difficulty: "Intermediate",
      role: "QA Engineer",
      evaluationFocus: "Curiosity, ownership, communication.",
      answerFramework: "Situation → instinct → investigation → confirmation → fix shipped → impact."
    },
    {
      id: "qa-q7",
      question: "How would you test a multi-step interview flow with audio recording?",
      type: "testing",
      difficulty: "Advanced",
      role: "QA Engineer",
      evaluationFocus: "Permissions, mocked devices, timing edge cases, accessibility.",
      answerFramework: "Permissions → device mocks → timing edge cases → resume + interruption → A11y."
    }
  ],
  ux: [
    {
      id: "ux-q1",
      question: "How would you redesign an interview results page so users know what to improve next?",
      type: "case",
      difficulty: "Intermediate",
      role: "UX Designer",
      evaluationFocus: "Information hierarchy, actionable feedback, accessibility, user goals.",
      answerFramework: "Clarify user outcome → audit current page → propose hierarchy → add next actions → validate with usability test."
    },
    {
      id: "ux-q2",
      question: "Describe a design decision where you balanced beauty and usability.",
      type: "behavioral",
      difficulty: "Intermediate",
      role: "UX Designer",
      evaluationFocus: "User-centered reasoning, constraints, iteration.",
      answerFramework: "Context → constraint → design alternatives → decision → result."
    },
    {
      id: "ux-q3",
      question: "How would you test whether users understand the difference between AI interview types?",
      type: "research",
      difficulty: "Intermediate",
      role: "UX Designer",
      evaluationFocus: "Research plan, tasks, success criteria, iteration.",
      answerFramework: "Define hypothesis → recruit target users → task-based test → measure confusion → iterate labels and UI."
    },
    {
      id: "ux-q4",
      question: "Critique the dashboard of a tool you use every day. Be specific.",
      type: "design critique",
      difficulty: "Intermediate",
      role: "UX Designer",
      evaluationFocus: "Hierarchy, IA, visual weight, jobs-to-be-done.",
      answerFramework: "Pick tool + JTBD → walk through current flow → call out friction → propose fixes → caveat assumptions."
    },
    {
      id: "ux-q5",
      question: "How do you advocate for accessibility when the team feels it's slowing the roadmap?",
      type: "behavioral",
      difficulty: "Advanced",
      role: "UX Designer",
      evaluationFocus: "Persuasion, evidence, priorities, empathy.",
      answerFramework: "Frame business + ethical case → quantify excluded users → low-cost wins first → measure → reinforce wins."
    },
    {
      id: "ux-q6",
      question: "Design an onboarding flow for a privacy-sensitive product.",
      type: "case",
      difficulty: "Advanced",
      role: "UX Designer",
      evaluationFocus: "Trust, progressive disclosure, transparency, dropout reduction.",
      answerFramework: "Identify trust moments → progressive disclosure → just-in-time consent → measure dropout → iterate."
    },
    {
      id: "ux-q7",
      question: "How would you redesign the AI interview results page so users know what to do next?",
      type: "case",
      difficulty: "Intermediate",
      role: "UX Designer",
      evaluationFocus: "Hierarchy, action orientation, accessibility, motivation.",
      answerFramework: "User goal → score interpretation → 3 next actions → motivation copy → accessibility check."
    }
  ],
  mobile: [
    {
      id: "mob-q1",
      question: "How would you architect offline-first sync for a mobile interview app?",
      type: "system design",
      difficulty: "Advanced",
      role: "Mobile Engineer",
      evaluationFocus: "Local persistence, conflict resolution, sync windows, retry strategy, network awareness.",
      answerFramework: "Clarify offline use case → choose local store → define sync model → handle conflicts → degrade UX gracefully when offline."
    },
    {
      id: "mob-q2",
      question: "A user reports the app crashes only on Android 12 with low memory. How do you debug?",
      type: "debugging",
      difficulty: "Intermediate",
      role: "Mobile Engineer",
      evaluationFocus: "Crash logs, version-specific APIs, memory profiling, reproduction strategy, instrumentation.",
      answerFramework: "Reproduce on target OS → fetch crash logs → memory profile → narrow to API change → patch + regression test."
    },
    {
      id: "mob-q3",
      question: "How do you keep a 60fps scroll experience on a long, image-heavy list?",
      type: "performance",
      difficulty: "Intermediate",
      role: "Mobile Engineer",
      evaluationFocus: "Recycler/lazy lists, image decoding, threading, prefetching, memory pressure.",
      answerFramework: "Use recycling lists → off-thread image decoding → cache strategy → prefetch ahead → measure with frame-time tools."
    },
    {
      id: "mob-q4",
      question: "Walk through a time you reduced your app's binary size or cold-start time.",
      type: "behavioral",
      difficulty: "Intermediate",
      role: "Mobile Engineer",
      evaluationFocus: "Measurement-driven optimization, tradeoffs, ownership, measurable impact.",
      answerFramework: "Situation → baseline metrics → audit assets/code → action → measurable outcome."
    },
    {
      id: "mob-q5",
      question: "Compare native, React Native, and Flutter for a startup launching on iOS and Android in 3 months.",
      type: "decision",
      difficulty: "Intermediate",
      role: "Mobile Engineer",
      evaluationFocus: "Tradeoffs, team skills, time-to-market, performance ceiling, hiring market.",
      answerFramework: "Clarify goals → list constraints → evaluate against criteria → recommend → name risks."
    },
    {
      id: "mob-q6",
      question: "How would you implement secure on-device storage for tokens and PII?",
      type: "security",
      difficulty: "Advanced",
      role: "Mobile Engineer",
      evaluationFocus: "Keystore/Keychain, encryption at rest, biometric gating, key rotation, threat model.",
      answerFramework: "Define threats → use platform secure storage → encrypt sensitive blobs → biometric gate → rotation policy."
    },
    {
      id: "mob-q7",
      question: "Push notifications stopped delivering for 5% of users. How do you triage?",
      type: "debugging",
      difficulty: "Intermediate",
      role: "Mobile Engineer",
      evaluationFocus: "APNs/FCM diagnostics, token lifecycle, server logs, user segmentation, partial outages.",
      answerFramework: "Segment affected users → check token validity → inspect server logs → reproduce → patch + monitor."
    },
    {
      id: "mob-q8",
      question: "Describe how you would A/B test a new onboarding flow on a mobile app.",
      type: "product",
      difficulty: "Intermediate",
      role: "Mobile Engineer",
      evaluationFocus: "Feature flags, sample size, success metrics, sample integrity, release cycle.",
      answerFramework: "Hypothesis → metric → flag rollout → segment → analyze → ship/rollback decision."
    },
    {
      id: "mob-q9",
      question: "How do you handle deep links that route to a screen requiring authentication?",
      type: "technical",
      difficulty: "Intermediate",
      role: "Mobile Engineer",
      evaluationFocus: "Routing graph, intent capture, post-login resumption, security, edge cases.",
      answerFramework: "Capture intent → auth gate → resume route post-login → cover background/cold start → guard against open redirects."
    },
    {
      id: "mob-q10",
      question: "Why do mobile teams adopt CI for instrumented tests and how would you set it up?",
      type: "system design",
      difficulty: "Intermediate",
      role: "Mobile Engineer",
      evaluationFocus: "Device farms, flake reduction, test pyramid, signing, parallelization.",
      answerFramework: "Pyramid first → unit + UI split → device farm or emulator matrix → signing pipeline → flake budget."
    }
  ],
  ml: [
    {
      id: "ml-q1",
      question: "Explain bias-variance tradeoff and how it shows up in model selection.",
      type: "technical",
      difficulty: "Intermediate",
      role: "ML Engineer",
      evaluationFocus: "Theory clarity, intuition, regularization, validation strategy.",
      answerFramework: "Define both → explain tradeoff → show via underfit/overfit example → mitigation strategies → validation curve."
    },
    {
      id: "ml-q2",
      question: "Walk me through deploying a model from notebook to production.",
      type: "system design",
      difficulty: "Advanced",
      role: "ML Engineer",
      evaluationFocus: "Reproducibility, packaging, serving, monitoring, rollback.",
      answerFramework: "Package model → serving stack → version + metadata → monitoring → drift detection → rollback plan."
    },
    {
      id: "ml-q3",
      question: "A model's offline metric is great but online metric drops 4%. What do you investigate?",
      type: "debugging",
      difficulty: "Advanced",
      role: "ML Engineer",
      evaluationFocus: "Train-serve skew, feature parity, label leakage, distribution shift.",
      answerFramework: "Check feature parity → label/freshness sanity → distribution diff → segment users → A/B with shadow."
    },
    {
      id: "ml-q4",
      question: "How would you choose between a simple logistic regression and a deep model for fraud detection?",
      type: "decision",
      difficulty: "Intermediate",
      role: "ML Engineer",
      evaluationFocus: "Data volume, latency, interpretability, ops cost, marginal accuracy.",
      answerFramework: "Define goals → data volume → latency budget → interpretability → cost vs gain → recommend."
    },
    {
      id: "ml-q5",
      question: "Tell me about a time your model failed silently in production.",
      type: "behavioral",
      difficulty: "Advanced",
      role: "ML Engineer",
      evaluationFocus: "Detection, ownership, root cause, prevention.",
      answerFramework: "Incident → detection gap → root cause → fix → guardrails added."
    },
    {
      id: "ml-q6",
      question: "Explain precision, recall, and F1 to a non-technical PM using a real example.",
      type: "communication",
      difficulty: "Beginner",
      role: "ML Engineer",
      evaluationFocus: "Plain language, real-world framing, when each matters.",
      answerFramework: "Choose example → define each → compare → say which to optimize and why."
    },
    {
      id: "ml-q7",
      question: "How would you build a recommendation system for interview prep content?",
      type: "system design",
      difficulty: "Advanced",
      role: "ML Engineer",
      evaluationFocus: "Cold start, embeddings, ranking, evaluation, feedback loop.",
      answerFramework: "Clarify goal → candidate gen → ranking → cold-start handling → eval offline + online → guardrails."
    },
    {
      id: "ml-q8",
      question: "What is data drift and how do you monitor for it?",
      type: "technical",
      difficulty: "Intermediate",
      role: "ML Engineer",
      evaluationFocus: "Statistical drift tests, feature monitoring, alerting thresholds, retraining cadence.",
      answerFramework: "Define types → choose metrics → set thresholds → automate alerts → retraining strategy."
    },
    {
      id: "ml-q9",
      question: "How do you design an experiment to test a new ranking model?",
      type: "experimentation",
      difficulty: "Advanced",
      role: "ML Engineer",
      evaluationFocus: "Hypothesis, randomization, sample size, guardrails, decision rule.",
      answerFramework: "Hypothesis → primary + guardrail metrics → sample size → run → decision rule → ship/rollback."
    },
    {
      id: "ml-q10",
      question: "Explain LLM fine-tuning vs RAG. When would you use which?",
      type: "technical",
      difficulty: "Advanced",
      role: "ML Engineer",
      evaluationFocus: "Cost, freshness, accuracy, ops, hybrid setups.",
      answerFramework: "Define both → cost/latency profile → freshness needs → accuracy ceiling → hybrid recommendation."
    }
  ],
  security: [
    {
      id: "sec-q1",
      question: "Walk through how you would threat-model an authentication system.",
      type: "system design",
      difficulty: "Advanced",
      role: "Security Engineer",
      evaluationFocus: "STRIDE/DREAD, attack surface, mitigations, prioritization.",
      answerFramework: "Inventory assets → list threats by category → rank by impact → mitigations → leftover risk."
    },
    {
      id: "sec-q2",
      question: "How would you respond to a suspected credential-stuffing attack right now?",
      type: "incident",
      difficulty: "Advanced",
      role: "Security Engineer",
      evaluationFocus: "Detection signals, containment, communication, forensic preservation, follow-up.",
      answerFramework: "Detect signals → contain (rate-limit/MFA) → notify stakeholders → forensic capture → post-mortem."
    },
    {
      id: "sec-q3",
      question: "Describe defense-in-depth for a public-facing web app.",
      type: "technical",
      difficulty: "Intermediate",
      role: "Security Engineer",
      evaluationFocus: "Layered controls, blast radius reduction, principle of least privilege.",
      answerFramework: "Edge controls → app controls → identity → data → monitoring → recovery."
    },
    {
      id: "sec-q4",
      question: "Tell me about a vulnerability you found and how you handled disclosure.",
      type: "behavioral",
      difficulty: "Advanced",
      role: "Security Engineer",
      evaluationFocus: "Discovery process, ethics, communication, remediation, lessons.",
      answerFramework: "Discovery → triage severity → responsible disclosure → fix tracking → post-mortem."
    },
    {
      id: "sec-q5",
      question: "Explain CSRF, XSS, and SSRF to a junior developer with code examples.",
      type: "communication",
      difficulty: "Intermediate",
      role: "Security Engineer",
      evaluationFocus: "Conceptual clarity, real attacks, mitigations, headers/tokens.",
      answerFramework: "Define each → minimal example → mitigations → defense-in-depth → testing approach."
    },
    {
      id: "sec-q6",
      question: "How do you secure secrets in a CI/CD pipeline?",
      type: "system design",
      difficulty: "Intermediate",
      role: "Security Engineer",
      evaluationFocus: "Secret stores, scoping, rotation, audit trails, ephemeral credentials.",
      answerFramework: "Centralize in vault → scope per job → rotate → audit → ephemeral creds where possible."
    },
    {
      id: "sec-q7",
      question: "A pen-tester found IDOR in your API. Walk through a fix.",
      type: "debugging",
      difficulty: "Advanced",
      role: "Security Engineer",
      evaluationFocus: "Authz model, object ownership checks, regression tests, blast radius.",
      answerFramework: "Reproduce → confirm authz model → patch with ownership check → add tests → audit similar endpoints."
    },
    {
      id: "sec-q8",
      question: "How would you secure a multi-tenant SaaS database?",
      type: "system design",
      difficulty: "Advanced",
      role: "Security Engineer",
      evaluationFocus: "Tenant isolation, row-level security, encryption, key strategy, audit.",
      answerFramework: "Tenant model → isolation strategy → encryption (at rest, in transit, per tenant key) → audit logs → DR."
    },
    {
      id: "sec-q9",
      question: "Explain how you would implement passwordless auth.",
      type: "technical",
      difficulty: "Intermediate",
      role: "Security Engineer",
      evaluationFocus: "WebAuthn/passkeys, magic links, recovery flows, threat model.",
      answerFramework: "Pick mechanism → enrollment → login → recovery → fallback + abuse handling."
    },
    {
      id: "sec-q10",
      question: "How do you keep dependencies safe across hundreds of repos?",
      type: "process",
      difficulty: "Intermediate",
      role: "Security Engineer",
      evaluationFocus: "SBOM, scanning, policy, auto-PRs, response SLA.",
      answerFramework: "SBOM → continuous scanning → severity policy → auto-update PRs → response SLA → metrics."
    }
  ],
  embedded: [
    {
      id: "emb-q1",
      question: "Explain how you would debounce a hardware button in firmware.",
      type: "technical",
      difficulty: "Beginner",
      role: "Embedded Engineer",
      evaluationFocus: "Timing, ISR vs polling, edge cases, testing approach.",
      answerFramework: "Define problem → sampling window → ISR vs poll → edge cases (long press) → on-device test."
    },
    {
      id: "emb-q2",
      question: "How do you choose between an RTOS and a bare-metal loop?",
      type: "decision",
      difficulty: "Intermediate",
      role: "Embedded Engineer",
      evaluationFocus: "Determinism, complexity, memory, power, future-proofing.",
      answerFramework: "List requirements → determinism vs complexity → memory budget → tradeoffs → recommend."
    },
    {
      id: "emb-q3",
      question: "A device draws too much current in sleep mode. How do you investigate?",
      type: "debugging",
      difficulty: "Advanced",
      role: "Embedded Engineer",
      evaluationFocus: "Power profiling, peripherals, GPIO leakage, regulator quiescent current.",
      answerFramework: "Measure baseline → disable peripherals one by one → audit GPIO states → regulator quiescent → patch + verify."
    },
    {
      id: "emb-q4",
      question: "How would you OTA-update firmware in the field safely?",
      type: "system design",
      difficulty: "Advanced",
      role: "Embedded Engineer",
      evaluationFocus: "A/B partitions, rollback, signing, brick prevention, bandwidth.",
      answerFramework: "Define safety goals → A/B partitions → signed images → rollback trigger → bandwidth strategy."
    },
    {
      id: "emb-q5",
      question: "Tell me about a time you optimized for memory in a constrained device.",
      type: "behavioral",
      difficulty: "Intermediate",
      role: "Embedded Engineer",
      evaluationFocus: "Profiling, tradeoffs, measurable impact.",
      answerFramework: "Situation → measurement → optimization choices → result → lesson learned."
    },
    {
      id: "emb-q6",
      question: "Compare DMA vs interrupt-driven I/O.",
      type: "technical",
      difficulty: "Intermediate",
      role: "Embedded Engineer",
      evaluationFocus: "Latency, throughput, CPU load, complexity.",
      answerFramework: "Define each → CPU load → throughput → typical use cases → caveats."
    },
    {
      id: "emb-q7",
      question: "How would you test firmware that interacts with sensors you don't have on your desk?",
      type: "process",
      difficulty: "Intermediate",
      role: "Embedded Engineer",
      evaluationFocus: "HIL, mocks, simulation, recorded captures.",
      answerFramework: "Layer abstraction → mock + record → HIL bench → CI strategy → field telemetry feedback."
    },
    {
      id: "emb-q8",
      question: "Explain how an I2C bus arbitration works.",
      type: "technical",
      difficulty: "Intermediate",
      role: "Embedded Engineer",
      evaluationFocus: "Open-drain lines, addressing, arbitration, clock stretching.",
      answerFramework: "Lines + pull-ups → addressing → multi-master arbitration → clock stretching → failure modes."
    },
    {
      id: "emb-q9",
      question: "How do you keep firmware bring-up time short for new hardware revisions?",
      type: "process",
      difficulty: "Intermediate",
      role: "Embedded Engineer",
      evaluationFocus: "Board-bringup checklist, peripheral abstraction, automation.",
      answerFramework: "Checklist → HAL discipline → smoke tests automated → pre-bringup simulation → post-mortem each rev."
    },
    {
      id: "emb-q10",
      question: "Describe how you would implement a watchdog that doesn't false-trigger.",
      type: "technical",
      difficulty: "Advanced",
      role: "Embedded Engineer",
      evaluationFocus: "Time budget, kicking strategy, sub-task health, recovery.",
      answerFramework: "Define dead-man criteria → per-task heartbeat → aggregator kicks → recovery on bite → telemetry."
    }
  ],
  cloud: [
    {
      id: "cld-q1",
      question: "Design multi-region failover for a globally available API.",
      type: "system design",
      difficulty: "Advanced",
      role: "Cloud Engineer",
      evaluationFocus: "Routing, replication, RTO/RPO, DNS, data consistency.",
      answerFramework: "Define RTO/RPO → active/passive vs active/active → replication strategy → routing → testing failover."
    },
    {
      id: "cld-q2",
      question: "How do you prevent surprise cloud bills?",
      type: "process",
      difficulty: "Intermediate",
      role: "Cloud Engineer",
      evaluationFocus: "Budgets/alerts, tagging, rightsizing, off-hours scaling, anomaly detection.",
      answerFramework: "Tag everything → budgets + alerts → rightsizing reports → schedule shutdowns → anomaly detection."
    },
    {
      id: "cld-q3",
      question: "Compare serverless vs containers for a high-throughput pipeline.",
      type: "decision",
      difficulty: "Intermediate",
      role: "Cloud Engineer",
      evaluationFocus: "Cost curves, cold start, ops overhead, debug experience.",
      answerFramework: "Define traffic profile → cost curve → cold start → ops surface → recommend → name escape hatches."
    },
    {
      id: "cld-q4",
      question: "An ECS/EKS deploy keeps failing health checks. How do you debug?",
      type: "debugging",
      difficulty: "Intermediate",
      role: "Cloud Engineer",
      evaluationFocus: "Probes, logs, networking, IAM, image issues.",
      answerFramework: "Confirm probe path → reproduce locally → logs → IAM/SG rules → image config drift → fix + monitor."
    },
    {
      id: "cld-q5",
      question: "How do you secure an S3 (or equivalent) bucket holding user uploads?",
      type: "security",
      difficulty: "Intermediate",
      role: "Cloud Engineer",
      evaluationFocus: "Bucket policy, encryption, signed URLs, access logging, malware scanning.",
      answerFramework: "Block public access → SSE → signed uploads → object scanning → access logs."
    },
    {
      id: "cld-q6",
      question: "Walk through a blue/green deployment for a stateful service.",
      type: "system design",
      difficulty: "Advanced",
      role: "Cloud Engineer",
      evaluationFocus: "Schema migrations, traffic shift, rollback, data sync.",
      answerFramework: "Backward-compatible migration → spin up green → drain traffic → verify → switch → rollback plan."
    },
    {
      id: "cld-q7",
      question: "Tell me about a time you reduced cloud costs without hurting reliability.",
      type: "behavioral",
      difficulty: "Intermediate",
      role: "Cloud Engineer",
      evaluationFocus: "Measurement, rightsizing, savings plans, reliability guardrails.",
      answerFramework: "Baseline cost → identify wins → action → measure savings → reliability guardrails preserved."
    },
    {
      id: "cld-q8",
      question: "How would you design a tenant-aware logging architecture?",
      type: "system design",
      difficulty: "Advanced",
      role: "Cloud Engineer",
      evaluationFocus: "Routing, retention, PII, query performance, cost.",
      answerFramework: "Tenant tags → routing → retention tiers → PII handling → indexing strategy."
    },
    {
      id: "cld-q9",
      question: "Explain IAM least privilege in a real engineering team.",
      type: "process",
      difficulty: "Intermediate",
      role: "Cloud Engineer",
      evaluationFocus: "Role design, break-glass, reviews, automation.",
      answerFramework: "Job-shape roles → temporary elevation → quarterly review → automation/CI gating → audit."
    },
    {
      id: "cld-q10",
      question: "How do you keep infrastructure-as-code drift from happening?",
      type: "process",
      difficulty: "Intermediate",
      role: "Cloud Engineer",
      evaluationFocus: "PR enforcement, drift detection, reconciliation, exceptions.",
      answerFramework: "PR-only changes → drift scanner → auto-reconcile → escape valves logged → metrics tracked."
    }
  ],
  sre: [
    {
      id: "sre-q1",
      question: "Define SLI, SLO, and SLA in your own words and explain the difference.",
      type: "technical",
      difficulty: "Intermediate",
      role: "Site Reliability Engineer",
      evaluationFocus: "Conceptual clarity, real examples, error budget link.",
      answerFramework: "Define each → real example → error budget link → tradeoff with feature velocity."
    },
    {
      id: "sre-q2",
      question: "Walk me through a postmortem for an incident you owned.",
      type: "behavioral",
      difficulty: "Advanced",
      role: "Site Reliability Engineer",
      evaluationFocus: "Blameless framing, root cause depth, action items, follow-through.",
      answerFramework: "Timeline → impact → root cause → contributing factors → action items + status."
    },
    {
      id: "sre-q3",
      question: "Latency p99 spiked but p50 is flat. What does this tell you?",
      type: "debugging",
      difficulty: "Advanced",
      role: "Site Reliability Engineer",
      evaluationFocus: "Tail latency causes, queueing, GC, slow tenants, retries.",
      answerFramework: "Confirm metric → segment by route/tenant → check queue + GC → retry storm? → patch + verify."
    },
    {
      id: "sre-q4",
      question: "How do you keep on-call sustainable for a small team?",
      type: "process",
      difficulty: "Intermediate",
      role: "Site Reliability Engineer",
      evaluationFocus: "Rotation, paging budget, runbooks, escalation, post-incident review cadence.",
      answerFramework: "Rotation design → paging budget → runbook discipline → escalation → review cadence + handoff."
    },
    {
      id: "sre-q5",
      question: "Explain capacity planning for a fast-growing API.",
      type: "system design",
      difficulty: "Advanced",
      role: "Site Reliability Engineer",
      evaluationFocus: "Forecasting, headroom, burst, autoscaling, dependency mapping.",
      answerFramework: "Load forecast → bottleneck inventory → headroom target → autoscale strategy → dependency limits."
    },
    {
      id: "sre-q6",
      question: "Describe how you would design a chaos-engineering program.",
      type: "process",
      difficulty: "Advanced",
      role: "Site Reliability Engineer",
      evaluationFocus: "Hypothesis-driven, blast radius, scheduling, learning.",
      answerFramework: "Pick hypothesis → small blast radius → run → measure → scale up → publish learnings."
    },
    {
      id: "sre-q7",
      question: "How do you pick what to alert on?",
      type: "process",
      difficulty: "Intermediate",
      role: "Site Reliability Engineer",
      evaluationFocus: "Customer-facing symptoms vs causes, paging discipline, false-positive cost.",
      answerFramework: "Symptom-based first → cause-based dashboards → page only actionable → measure FP rate → prune."
    },
    {
      id: "sre-q8",
      question: "Tell me about a time you migrated a noisy alert system to be quiet.",
      type: "behavioral",
      difficulty: "Intermediate",
      role: "Site Reliability Engineer",
      evaluationFocus: "Stakeholder buy-in, measurement, prioritization.",
      answerFramework: "Baseline noise → buy-in → reclassify → reduce → measure → reinforce."
    },
    {
      id: "sre-q9",
      question: "Explain how a circuit breaker improves a microservice mesh.",
      type: "technical",
      difficulty: "Intermediate",
      role: "Site Reliability Engineer",
      evaluationFocus: "Failure isolation, retry storms, half-open state, fallbacks.",
      answerFramework: "Define states → trip conditions → fallback → half-open recovery → metrics."
    },
    {
      id: "sre-q10",
      question: "What's your approach to runbooks in 2026?",
      type: "process",
      difficulty: "Intermediate",
      role: "Site Reliability Engineer",
      evaluationFocus: "Living docs, automation, AI assistance, drill cadence.",
      answerFramework: "Living source → automate steps → AI-assist triage → tabletop drills → ownership tags."
    }
  ],
  engmgr: [
    {
      id: "em-q1",
      question: "Tell me about a time you turned around an underperforming engineer.",
      type: "behavioral",
      difficulty: "Advanced",
      role: "Engineering Manager",
      evaluationFocus: "Diagnosis, support, expectations, outcome, empathy.",
      answerFramework: "Situation → diagnose root cause → set expectations → support plan → outcome + learning."
    },
    {
      id: "em-q2",
      question: "How do you prioritize when product wants 10 features and you have 4 engineers?",
      type: "process",
      difficulty: "Advanced",
      role: "Engineering Manager",
      evaluationFocus: "Stack-rank framework, communication, tradeoffs, capacity, opportunity cost.",
      answerFramework: "Clarify goals → impact/effort score → present tradeoffs → align with PM → commit + reassess monthly."
    },
    {
      id: "em-q3",
      question: "Describe how you've grown a senior engineer into a tech lead.",
      type: "behavioral",
      difficulty: "Advanced",
      role: "Engineering Manager",
      evaluationFocus: "Coaching, opportunity-creation, feedback loops, measurable growth.",
      answerFramework: "Identify potential → growth plan → stretch project → feedback cadence → measurable outcome."
    },
    {
      id: "em-q4",
      question: "How do you run a productive 1:1?",
      type: "process",
      difficulty: "Intermediate",
      role: "Engineering Manager",
      evaluationFocus: "Cadence, agenda ownership, listening, action items, career signal.",
      answerFramework: "Cadence → engineer owns agenda → listening → action items → career conversation cadence."
    },
    {
      id: "em-q5",
      question: "Walk me through a hire you regret and what you changed afterward.",
      type: "behavioral",
      difficulty: "Advanced",
      role: "Engineering Manager",
      evaluationFocus: "Self-awareness, signal calibration, process change.",
      answerFramework: "Decision context → signal missed → impact → process change → measurable improvement."
    },
    {
      id: "em-q6",
      question: "How do you balance writing code yourself and managing the team?",
      type: "decision",
      difficulty: "Intermediate",
      role: "Engineering Manager",
      evaluationFocus: "Time allocation, leverage, when to step in, career stage of team.",
      answerFramework: "Default mode (no, unless) → pick small surface → time-box → measure leverage."
    },
    {
      id: "em-q7",
      question: "Your team missed a deadline. How do you handle it with stakeholders?",
      type: "behavioral",
      difficulty: "Intermediate",
      role: "Engineering Manager",
      evaluationFocus: "Communication, accountability, recovery plan, learning.",
      answerFramework: "Communicate early → own it → recovery plan with new estimate → root cause → preventive change."
    },
    {
      id: "em-q8",
      question: "Two senior engineers disagree on architecture. How do you resolve it?",
      type: "process",
      difficulty: "Advanced",
      role: "Engineering Manager",
      evaluationFocus: "Decision frameworks, psychological safety, accountability.",
      answerFramework: "Define decision criteria → ask for written tradeoffs → time-box → decide (DACI/RACI) → document."
    },
    {
      id: "em-q9",
      question: "How do you measure your team's health?",
      type: "process",
      difficulty: "Intermediate",
      role: "Engineering Manager",
      evaluationFocus: "Quant + qual signals, retros, eNPS, attrition, throughput.",
      answerFramework: "Quant signals → qual signals (retros, 1:1s) → trend over time → action when red."
    },
    {
      id: "em-q10",
      question: "Tell me about a time you advocated for an unpopular technical investment.",
      type: "behavioral",
      difficulty: "Advanced",
      role: "Engineering Manager",
      evaluationFocus: "Persuasion, evidence, business framing, follow-through.",
      answerFramework: "Problem → business framing → data + ROI → champion + commit → outcome → lesson."
    }
  ]
};

export const REAL_LIBRARY_RESOURCES = [
  {
    id: "real-video-star-method-indeed",
    tabKey: "video",
    categoryId: "video",
    type: "Video Tutorial",
    title: "STAR Method: answer behavioral questions clearly",
    description: "A real YouTube lesson on structuring behavioral answers with Situation, Task, Action, and Result.",
    thumbnail: yt("OMhr7SJCpmQ"),
    youtubeId: "OMhr7SJCpmQ",
    youtubeUrl: "https://www.youtube.com/watch?v=OMhr7SJCpmQ",
    sourceName: "YouTube",
    role: "All Roles",
    interviewType: "behavioral",
    industry: "tech",
    skillLevel: "beginner",
    durationKey: "short",
    difficulty: "Beginner",
    duration: "10 min",
    completions: "8.2k",
    rating: 4.8,
    isNew: true,
    isPremium: false,
    isDownloadable: false,
    tags: ["STAR", "behavioral", "communication", "confidence"],
    questions: [...qs.fullstack.slice(3), ...qs.pm.slice(2), ...qs.ux.slice(1, 2)],
    learningOutcomes: [
      "Use a repeatable STAR answer structure",
      "Turn vague stories into measurable impact",
      "Practice concise answers under time pressure"
    ],
    prerequisites: "Bring 2–3 real projects, school projects, internships, or freelance examples.",
    contentSections: [
      { title: "Watch the STAR breakdown", duration: "10 min", type: "video", completed: false },
      { title: "Write 3 STAR stories", duration: "15 min", type: "interactive", completed: false },
      { title: "Practice behavioral questions", duration: "20 min", type: "practice", completed: false }
    ]
  },
  {
    id: "real-video-system-design-youtube",
    tabKey: "video",
    categoryId: "video",
    type: "Video Tutorial",
    title: "System Design Interview: Design YouTube",
    description: "A real system-design walkthrough for video platforms: requirements, architecture, storage, scaling, and tradeoffs.",
    thumbnail: yt("IUrQ5_g3XKs"),
    youtubeId: "IUrQ5_g3XKs",
    youtubeUrl: "https://www.youtube.com/watch?v=IUrQ5_g3XKs",
    sourceName: "YouTube",
    role: "Backend Developer",
    interviewType: "system design",
    industry: "tech",
    skillLevel: "advanced",
    durationKey: "long",
    difficulty: "Advanced",
    duration: "45 min",
    completions: "12.4k",
    rating: 4.9,
    isNew: false,
    isPremium: false,
    isDownloadable: false,
    tags: ["system design", "backend", "scalability", "architecture"],
    questions: [
      ...qs.backend.slice(1, 3),
      {
        id: "sd-q-youtube-1",
        question: "Design a video upload and streaming system for millions of users.",
        type: "system design",
        difficulty: "Advanced",
        role: "Backend Developer",
        evaluationFocus: "Requirements, storage, transcoding, CDN, metadata, scalability.",
        answerFramework: "Clarify scope → APIs → high-level architecture → storage/transcoding/CDN → bottlenecks → tradeoffs."
      },
      {
        id: "sd-q-youtube-2",
        question: "How would you handle video processing failures and retries?",
        type: "system design",
        difficulty: "Advanced",
        role: "Backend Developer",
        evaluationFocus: "Queues, idempotency, status transitions, retries, monitoring.",
        answerFramework: "Define states → queue processing → idempotent workers → retry policy → user-facing status → alerts."
      }
    ],
    learningOutcomes: [
      "Break down system-design prompts into requirements",
      "Explain video upload, processing, and delivery flows",
      "Discuss real tradeoffs like latency, cost, and reliability"
    ],
    prerequisites: "Basic knowledge of APIs, databases, queues, object storage, and CDN concepts.",
    contentSections: [
      { title: "Requirements and scope", duration: "8 min", type: "video", completed: false },
      { title: "High-level architecture notes", duration: "15 min", type: "text", completed: false },
      { title: "Practice: design video upload", duration: "35 min", type: "practice", completed: false }
    ]
  },
  {
    id: "real-video-js-questions-2025",
    tabKey: "video",
    categoryId: "video",
    type: "Video Tutorial",
    title: "Top JavaScript Interview Questions",
    description: "A real YouTube JavaScript interview resource for closures, async behavior, hoisting, prototypes, and common frontend topics.",
    thumbnail: yt("MX48mv73jf8"),
    youtubeId: "MX48mv73jf8",
    youtubeUrl: "https://www.youtube.com/watch?v=MX48mv73jf8",
    sourceName: "YouTube",
    role: "Frontend Developer",
    interviewType: "technical",
    industry: "tech",
    skillLevel: "intermediate",
    durationKey: "medium",
    difficulty: "Intermediate",
    duration: "30 min",
    completions: "10.7k",
    rating: 4.7,
    isNew: true,
    isPremium: false,
    isDownloadable: false,
    tags: ["JavaScript", "React", "frontend", "technical"],
    questions: qs.frontend,
    learningOutcomes: [
      "Explain JavaScript fundamentals clearly",
      "Answer React form/state questions",
      "Debug frontend issues step by step"
    ],
    prerequisites: "Basic JavaScript, React components, and browser devtools.",
    contentSections: [
      { title: "JavaScript fundamentals video", duration: "30 min", type: "video", completed: false },
      { title: "React + browser debugging questions", duration: "20 min", type: "practice", completed: false },
      { title: "Answer review checklist", duration: "8 min", type: "text", completed: false }
    ]
  },
  {
    id: "real-video-pm-frameworks",
    tabKey: "video",
    categoryId: "video",
    type: "Video Tutorial",
    title: "Answer Product Management Interview Questions",
    description: "A real YouTube PM prep video focused on frameworks for product sense, prioritization, execution, and communication.",
    thumbnail: yt("AHcGlkALJ30"),
    youtubeId: "AHcGlkALJ30",
    youtubeUrl: "https://www.youtube.com/watch?v=AHcGlkALJ30",
    sourceName: "YouTube",
    role: "Product Manager",
    interviewType: "product case",
    industry: "tech",
    skillLevel: "intermediate",
    durationKey: "medium",
    difficulty: "Intermediate",
    duration: "25 min",
    completions: "6.9k",
    rating: 4.8,
    isNew: false,
    isPremium: false,
    isDownloadable: false,
    tags: ["product management", "prioritization", "metrics", "strategy"],
    questions: qs.pm,
    learningOutcomes: [
      "Structure product case answers",
      "Use metrics to support product decisions",
      "Communicate tradeoffs like a PM"
    ],
    prerequisites: "Know one product you use well and be ready to improve it.",
    contentSections: [
      { title: "PM framework video", duration: "25 min", type: "video", completed: false },
      { title: "Product sense drill", duration: "20 min", type: "practice", completed: false },
      { title: "Prioritization scorecard", duration: "10 min", type: "interactive", completed: false }
    ]
  },
  {
    id: "qb-fullstack-ai-saas",
    tabKey: "question",
    categoryId: "question",
    type: "Question Bank",
    title: "Full Stack Developer: SaaS interview bank",
    description: "Real-world full-stack prompts around React, Flask/Node APIs, auth, databases, debugging, and performance.",
    thumbnail: "https://images.unsplash.com/photo-1555066931-4365d14bab8c?auto=format&fit=crop&w=900&q=80",
    role: "Full Stack Developer",
    interviewType: "technical",
    industry: "tech",
    skillLevel: "intermediate",
    durationKey: "medium",
    difficulty: "Intermediate",
    duration: "35 min",
    completions: "9.1k",
    rating: 4.9,
    isNew: true,
    isPremium: false,
    isDownloadable: true,
    tags: ["React", "Flask", "SQL", "auth", "debugging"],
    questions: qs.fullstack,
    learningOutcomes: [
      "Connect frontend symptoms to backend and database causes",
      "Explain API design decisions with confidence",
      "Prepare for real SaaS engineering interviews"
    ],
    prerequisites: "Basic React, REST APIs, and SQL.",
    contentSections: [
      { title: "Authentication questions", duration: "10 min", type: "practice", completed: false },
      { title: "API debugging scenarios", duration: "15 min", type: "practice", completed: false },
      { title: "Performance and scaling prompts", duration: "10 min", type: "practice", completed: false }
    ]
  },
  {
    id: "qb-frontend-react-js",
    tabKey: "question",
    categoryId: "question",
    type: "Question Bank",
    title: "Frontend Developer: React + JavaScript questions",
    description: "Role-specific frontend interview questions for React state, rendering, accessibility, performance, and debugging.",
    thumbnail: "https://images.unsplash.com/photo-1627398242454-45a1465c2479?auto=format&fit=crop&w=900&q=80",
    role: "Frontend Developer",
    interviewType: "technical",
    industry: "tech",
    skillLevel: "intermediate",
    durationKey: "medium",
    difficulty: "Intermediate",
    duration: "30 min",
    completions: "7.8k",
    rating: 4.8,
    isNew: false,
    isPremium: false,
    isDownloadable: true,
    tags: ["React", "JavaScript", "accessibility", "performance"],
    questions: qs.frontend,
    learningOutcomes: [
      "Explain core React concepts clearly",
      "Debug real frontend issues",
      "Talk about accessibility and performance like a pro"
    ],
    prerequisites: "JavaScript fundamentals and basic React hooks.",
    contentSections: [
      { title: "React foundations", duration: "10 min", type: "practice", completed: false },
      { title: "Performance drill", duration: "10 min", type: "practice", completed: false },
      { title: "Accessibility drill", duration: "10 min", type: "practice", completed: false }
    ]
  },
  {
    id: "qb-backend-api-db",
    tabKey: "question",
    categoryId: "question",
    type: "Question Bank",
    title: "Backend Developer: API, SQL, scaling, and reliability",
    description: "Backend interview set for API design, database indexes, rate limiting, subscriptions, and production debugging.",
    thumbnail: "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?auto=format&fit=crop&w=900&q=80",
    role: "Backend Developer",
    interviewType: "technical",
    industry: "tech",
    skillLevel: "advanced",
    durationKey: "medium",
    difficulty: "Advanced",
    duration: "40 min",
    completions: "6.3k",
    rating: 4.9,
    isNew: false,
    isPremium: true,
    isDownloadable: true,
    tags: ["API", "SQL", "Redis", "rate limiting", "reliability"],
    questions: qs.backend,
    learningOutcomes: [
      "Design backend endpoints safely",
      "Explain database indexing tradeoffs",
      "Handle production failures calmly"
    ],
    prerequisites: "REST APIs, SQL, auth, and basic Redis concepts.",
    contentSections: [
      { title: "API design drill", duration: "12 min", type: "practice", completed: false },
      { title: "Database indexing drill", duration: "12 min", type: "practice", completed: false },
      { title: "Production incident drill", duration: "16 min", type: "practice", completed: false }
    ]
  },
  {
    id: "pack-data-analyst-sql",
    tabKey: "pack",
    categoryId: "pack",
    type: "Mock Pack",
    title: "Data Analyst: SQL + metrics case pack",
    description: "A practical mock pack for SQL, funnel metrics, retention, data quality, and business analysis interviews.",
    thumbnail: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=900&q=80",
    role: "Data Analyst",
    interviewType: "case",
    industry: "finance",
    skillLevel: "intermediate",
    durationKey: "long",
    difficulty: "Intermediate",
    duration: "50 min",
    completions: "4.8k",
    rating: 4.7,
    isNew: true,
    isPremium: false,
    isDownloadable: true,
    tags: ["SQL", "analytics", "retention", "dashboards"],
    questions: qs.data,
    learningOutcomes: [
      "Answer SQL prompts with clear assumptions",
      "Investigate metric drops methodically",
      "Connect analysis to product decisions"
    ],
    prerequisites: "Basic SQL joins, aggregation, and funnel concepts.",
    contentSections: [
      { title: "SQL prompt", duration: "15 min", type: "practice", completed: false },
      { title: "Metric-drop case", duration: "20 min", type: "practice", completed: false },
      { title: "Retention analysis case", duration: "15 min", type: "practice", completed: false }
    ]
  },
  {
    id: "pack-devops-render-redis",
    tabKey: "pack",
    categoryId: "pack",
    type: "Mock Pack",
    title: "DevOps Engineer: Deployments, workers, and monitoring",
    description: "Interview practice for runtime crashes, CI/CD, Redis queues, worker monitoring, and production reliability.",
    thumbnail: "https://images.unsplash.com/photo-1667372393119-3d4c48d07fc9?auto=format&fit=crop&w=900&q=80",
    role: "DevOps Engineer",
    interviewType: "system design",
    industry: "tech",
    skillLevel: "advanced",
    durationKey: "medium",
    difficulty: "Advanced",
    duration: "35 min",
    completions: "3.9k",
    rating: 4.8,
    isNew: false,
    isPremium: true,
    isDownloadable: true,
    tags: ["CI/CD", "Render", "Redis", "workers", "monitoring"],
    questions: qs.devops,
    learningOutcomes: [
      "Debug failed deployments with logs and environment checks",
      "Design safer CI/CD pipelines",
      "Monitor queues and background jobs"
    ],
    prerequisites: "Basic cloud deployment, environment variables, and queue concepts.",
    contentSections: [
      { title: "Runtime crash scenario", duration: "10 min", type: "practice", completed: false },
      { title: "CI/CD design scenario", duration: "15 min", type: "practice", completed: false },
      { title: "Worker monitoring scenario", duration: "10 min", type: "practice", completed: false }
    ]
  },
  {
    id: "qb-qa-automation",
    tabKey: "question",
    categoryId: "question",
    type: "Question Bank",
    title: "QA Engineer: E2E, regression, and production bugs",
    description: "Testing interview questions for end-to-end flows, regression coverage, plan-specific bugs, and safe reproduction.",
    thumbnail: "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&w=900&q=80",
    role: "QA Engineer",
    interviewType: "testing",
    industry: "tech",
    skillLevel: "intermediate",
    durationKey: "medium",
    difficulty: "Intermediate",
    duration: "30 min",
    completions: "2.7k",
    rating: 4.6,
    isNew: false,
    isPremium: false,
    isDownloadable: true,
    tags: ["QA", "E2E", "regression", "testing"],
    questions: qs.qa,
    learningOutcomes: [
      "Plan complete E2E coverage",
      "Turn bugs into regression tests",
      "Reproduce production issues safely"
    ],
    prerequisites: "Basic test planning and browser/API testing knowledge.",
    contentSections: [
      { title: "E2E flow test", duration: "12 min", type: "practice", completed: false },
      { title: "Regression test design", duration: "10 min", type: "practice", completed: false },
      { title: "Production bug reproduction", duration: "8 min", type: "practice", completed: false }
    ]
  },
  {
    id: "pack-ux-results-page",
    tabKey: "pack",
    categoryId: "pack",
    type: "Mock Pack",
    title: "UX Designer: AI interview results case",
    description: "A UX design case pack for improving analytics, feedback clarity, next actions, and confidence-building flows.",
    thumbnail: "https://images.unsplash.com/photo-1581291518857-4e27b48ff24e?auto=format&fit=crop&w=900&q=80",
    role: "UX Designer",
    interviewType: "case",
    industry: "tech",
    skillLevel: "intermediate",
    durationKey: "medium",
    difficulty: "Intermediate",
    duration: "35 min",
    completions: "2.1k",
    rating: 4.7,
    isNew: true,
    isPremium: false,
    isDownloadable: true,
    tags: ["UX", "product design", "research", "analytics"],
    questions: qs.ux,
    learningOutcomes: [
      "Structure a UX case answer",
      "Connect design to measurable user outcomes",
      "Explain usability validation plans"
    ],
    prerequisites: "Basic product design process and usability-testing concepts.",
    contentSections: [
      { title: "Design case prompt", duration: "15 min", type: "practice", completed: false },
      { title: "Behavioral design question", duration: "10 min", type: "practice", completed: false },
      { title: "Research plan question", duration: "10 min", type: "practice", completed: false }
    ]
  },
  {
    id: "path-job-ready-engineer",
    tabKey: "path",
    categoryId: "path",
    type: "Learning Path",
    title: "7-Day Job-Ready Engineer Path",
    description: "A focused path combining behavioral, frontend, backend, debugging, and system-design practice.",
    thumbnail: "https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=900&q=80",
    role: "Full Stack Developer",
    interviewType: "mixed",
    industry: "tech",
    skillLevel: "intermediate",
    durationKey: "extended",
    difficulty: "Intermediate",
    duration: "7 days",
    completions: "11.5k",
    rating: 4.9,
    isNew: false,
    isPremium: true,
    isDownloadable: true,
    tags: ["learning path", "full stack", "behavioral", "system design"],
    questions: [...qs.fullstack, ...qs.frontend.slice(0, 2), ...qs.backend.slice(0, 2)],
    learningOutcomes: [
      "Build a complete interview prep routine",
      "Practice mixed interview types",
      "Track weak areas before real interviews"
    ],
    prerequisites: "One hour per day for one week.",
    contentSections: [
      { title: "Day 1: STAR stories", duration: "45 min", type: "interactive", completed: false },
      { title: "Day 2: JavaScript/React", duration: "60 min", type: "practice", completed: false },
      { title: "Day 3: APIs and databases", duration: "60 min", type: "practice", completed: false },
      { title: "Day 4: Debugging", duration: "45 min", type: "practice", completed: false },
      { title: "Day 5: System design", duration: "60 min", type: "practice", completed: false },
      { title: "Day 6: Mock interview", duration: "45 min", type: "practice", completed: false },
      { title: "Day 7: Review and repeat", duration: "45 min", type: "text", completed: false }
    ]
  },
  {
    id: "doc-star-answer-template",
    tabKey: "pdf",
    categoryId: "pdf",
    type: "Doc Guide",
    title: "Behavioral Answer Template: STAR + metrics",
    description: "A concise guide for turning real experience into strong interview stories with measurable impact.",
    thumbnail: "https://images.unsplash.com/photo-1455390582262-044cdead277a?auto=format&fit=crop&w=900&q=80",
    role: "All Roles",
    interviewType: "behavioral",
    industry: "consulting",
    skillLevel: "beginner",
    durationKey: "short",
    difficulty: "Beginner",
    duration: "12 min",
    completions: "14.2k",
    rating: 4.8,
    isNew: false,
    isPremium: false,
    isDownloadable: true,
    tags: ["STAR", "behavioral", "template", "storytelling"],
    questions: [...qs.fullstack.slice(3), ...qs.pm.slice(2), ...qs.ux.slice(1, 2)],
    learningOutcomes: [
      "Create reusable behavioral stories",
      "Add metrics and outcomes to answers",
      "Avoid rambling under pressure"
    ],
    prerequisites: "Any real project, team, school, or work experience.",
    contentSections: [
      { title: "STAR story worksheet", duration: "6 min", type: "text", completed: false },
      { title: "Metrics checklist", duration: "4 min", type: "text", completed: false },
      { title: "Practice prompts", duration: "12 min", type: "practice", completed: false }
    ]
  },
  {
    id: "company-kit-faang-style",
    tabKey: "company_kit",
    categoryId: "company_kit",
    type: "Company Kit",
    title: "FAANG-style software interview kit",
    description: "A practical kit for big-tech-style interviews: coding, systems, behavioral, communication, and tradeoff questions.",
    thumbnail: "https://images.unsplash.com/photo-1504384308090-c894fdcc538d?auto=format&fit=crop&w=900&q=80",
    role: "Software Engineer",
    interviewType: "mixed",
    industry: "tech",
    skillLevel: "advanced",
    durationKey: "extended",
    difficulty: "Advanced",
    duration: "2 hours",
    completions: "8.6k",
    rating: 4.9,
    isNew: true,
    isPremium: true,
    isDownloadable: true,
    tags: ["big tech", "coding", "system design", "behavioral"],
    questions: [...qs.frontend, ...qs.backend, ...qs.fullstack],
    learningOutcomes: [
      "Prepare for multi-round software interviews",
      "Practice explaining tradeoffs",
      "Combine technical and behavioral readiness"
    ],
    prerequisites: "Comfortable with coding basics, APIs, databases, and one substantial project.",
    contentSections: [
      { title: "Round 1: coding fundamentals", duration: "30 min", type: "practice", completed: false },
      { title: "Round 2: debugging and architecture", duration: "30 min", type: "practice", completed: false },
      { title: "Round 3: system design", duration: "35 min", type: "practice", completed: false },
      { title: "Round 4: behavioral", duration: "25 min", type: "practice", completed: false }
    ]
  },

  /* ─────────────────────────── ADDITIONAL RESOURCES ─────────────────────────── */
  /* Question banks for new roles                                                 */

  {
    id: "qb-mobile-engineer",
    tabKey: "question",
    categoryId: "question",
    type: "Question Bank",
    title: "Mobile Engineer Question Bank",
    description: "Architecture, performance, security, and behavioral questions for iOS, Android, React Native, and Flutter engineers.",
    thumbnail: "https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?auto=format&fit=crop&w=900&q=70",
    sourceName: "InterviewGenieAI",
    role: "Mobile Engineer",
    interviewType: "mixed",
    industry: "tech",
    skillLevel: "intermediate",
    durationKey: "medium",
    difficulty: "Intermediate",
    duration: "45 min",
    completions: "3.7k",
    rating: 4.7,
    isNew: true,
    isPremium: false,
    isDownloadable: true,
    tags: ["mobile", "iOS", "Android", "React Native", "Flutter", "performance"],
    questions: [...qs.mobile, ...qs.frontend.slice(0, 2)],
    learningOutcomes: [
      "Discuss offline-first sync, performance, and security with confidence",
      "Pick the right native vs cross-platform tradeoff for a given product",
      "Practice debugging stories with measurable outcomes"
    ],
    prerequisites: "Built and shipped at least one mobile app (production, side project, or coursework).",
    contentSections: [
      { title: "Architecture and tradeoffs", duration: "15 min", type: "practice", completed: false },
      { title: "Performance and offline", duration: "15 min", type: "practice", completed: false },
      { title: "Security and behavioral", duration: "15 min", type: "practice", completed: false }
    ]
  },
  {
    id: "qb-ml-engineer",
    tabKey: "question",
    categoryId: "question",
    type: "Question Bank",
    title: "ML Engineer Question Bank",
    description: "From bias-variance to LLM fine-tuning vs RAG. Real interview prompts for MLEs covering modeling, ops, and ethics.",
    thumbnail: "https://images.unsplash.com/photo-1620712943543-bcc4688e7485?auto=format&fit=crop&w=900&q=70",
    sourceName: "InterviewGenieAI",
    role: "ML Engineer",
    interviewType: "mixed",
    industry: "tech",
    skillLevel: "advanced",
    durationKey: "medium",
    difficulty: "Advanced",
    duration: "50 min",
    completions: "4.1k",
    rating: 4.8,
    isNew: true,
    isPremium: true,
    isDownloadable: true,
    tags: ["ML", "AI", "MLOps", "LLM", "evaluation"],
    questions: [...qs.ml, ...qs.data.slice(0, 2)],
    learningOutcomes: [
      "Explain offline vs online metrics and how to debug train-serve skew",
      "Design a recommender or ranking system end-to-end",
      "Decide between fine-tuning and RAG with cost/latency awareness"
    ],
    prerequisites: "Comfort with at least one ML framework (sklearn, PyTorch, TF) and basic deployment.",
    contentSections: [
      { title: "Modeling fundamentals", duration: "15 min", type: "practice", completed: false },
      { title: "MLOps and monitoring", duration: "20 min", type: "practice", completed: false },
      { title: "LLM and modern systems", duration: "15 min", type: "practice", completed: false }
    ]
  },
  {
    id: "qb-security-engineer",
    tabKey: "question",
    categoryId: "question",
    type: "Question Bank",
    title: "Security Engineer Question Bank",
    description: "Threat modeling, incident response, IDOR/CSRF/XSS, secrets, and multi-tenant security for application-security engineers.",
    thumbnail: "https://images.unsplash.com/photo-1550751827-4bd374c3f58b?auto=format&fit=crop&w=900&q=70",
    sourceName: "InterviewGenieAI",
    role: "Security Engineer",
    interviewType: "mixed",
    industry: "tech",
    skillLevel: "advanced",
    durationKey: "medium",
    difficulty: "Advanced",
    duration: "45 min",
    completions: "2.4k",
    rating: 4.7,
    isNew: true,
    isPremium: true,
    isDownloadable: true,
    tags: ["security", "appsec", "cloud security", "incident response"],
    questions: [...qs.security, ...qs.devops.slice(0, 2)],
    learningOutcomes: [
      "Lead a structured threat-modeling conversation",
      "Walk through incident response without losing composure",
      "Explain authn/z and secrets management to mixed audiences"
    ],
    prerequisites: "Familiarity with web fundamentals, OAuth/OIDC, and cloud IAM basics.",
    contentSections: [
      { title: "Threat modeling and design", duration: "15 min", type: "practice", completed: false },
      { title: "Incident response drills", duration: "15 min", type: "practice", completed: false },
      { title: "Vulnerabilities and patches", duration: "15 min", type: "practice", completed: false }
    ]
  },
  {
    id: "qb-cloud-engineer",
    tabKey: "question",
    categoryId: "question",
    type: "Question Bank",
    title: "Cloud Engineer Question Bank",
    description: "Multi-region, cost control, IAM, IaC, and serverless-vs-container decisions for AWS/GCP/Azure engineers.",
    thumbnail: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&w=900&q=70",
    sourceName: "InterviewGenieAI",
    role: "Cloud Engineer",
    interviewType: "mixed",
    industry: "tech",
    skillLevel: "intermediate",
    durationKey: "medium",
    difficulty: "Intermediate",
    duration: "45 min",
    completions: "3.0k",
    rating: 4.7,
    isNew: false,
    isPremium: false,
    isDownloadable: true,
    tags: ["cloud", "AWS", "GCP", "Azure", "IaC", "FinOps"],
    questions: [...qs.cloud, ...qs.devops.slice(2, 4)],
    learningOutcomes: [
      "Reason about RTO/RPO and multi-region failover concretely",
      "Defend cost optimizations without compromising reliability",
      "Explain IaC drift, IAM least-privilege, and tenant-aware logging"
    ],
    prerequisites: "Day-to-day experience with at least one major cloud provider.",
    contentSections: [
      { title: "Architecture and reliability", duration: "15 min", type: "practice", completed: false },
      { title: "Security and IAM", duration: "15 min", type: "practice", completed: false },
      { title: "FinOps and ops", duration: "15 min", type: "practice", completed: false }
    ]
  },
  {
    id: "qb-sre-engineer",
    tabKey: "question",
    categoryId: "question",
    type: "Question Bank",
    title: "Site Reliability Engineer Question Bank",
    description: "SLOs, postmortems, p99 debugging, capacity planning, on-call sustainability, and chaos engineering.",
    thumbnail: "https://images.unsplash.com/photo-1573164713988-8665fc963095?auto=format&fit=crop&w=900&q=70",
    sourceName: "InterviewGenieAI",
    role: "Site Reliability Engineer",
    interviewType: "mixed",
    industry: "tech",
    skillLevel: "advanced",
    durationKey: "medium",
    difficulty: "Advanced",
    duration: "50 min",
    completions: "2.8k",
    rating: 4.8,
    isNew: true,
    isPremium: false,
    isDownloadable: true,
    tags: ["SRE", "reliability", "SLO", "incident", "on-call"],
    questions: [...qs.sre, ...qs.cloud.slice(0, 2)],
    learningOutcomes: [
      "Talk fluently about SLI/SLO/SLA and error budgets",
      "Lead a blameless postmortem from timeline to action items",
      "Diagnose tail-latency issues and design alerting that doesn't burn the team"
    ],
    prerequisites: "Production on-call experience or a recent incident you owned.",
    contentSections: [
      { title: "SLOs and error budgets", duration: "15 min", type: "practice", completed: false },
      { title: "Incident and debugging", duration: "20 min", type: "practice", completed: false },
      { title: "On-call and process", duration: "15 min", type: "practice", completed: false }
    ]
  },
  {
    id: "qb-engineering-manager",
    tabKey: "question",
    categoryId: "question",
    type: "Question Bank",
    title: "Engineering Manager Question Bank",
    description: "Performance management, prioritization, hiring, conflict resolution, and team-health questions for first-time and senior EMs.",
    thumbnail: "https://images.unsplash.com/photo-1521737711867-e3b97375f902?auto=format&fit=crop&w=900&q=70",
    sourceName: "InterviewGenieAI",
    role: "Engineering Manager",
    interviewType: "behavioral",
    industry: "tech",
    skillLevel: "advanced",
    durationKey: "long",
    difficulty: "Advanced",
    duration: "55 min",
    completions: "4.6k",
    rating: 4.9,
    isNew: true,
    isPremium: true,
    isDownloadable: true,
    tags: ["leadership", "management", "behavioral", "FAANG"],
    questions: [...qs.engmgr, ...qs.pm.slice(0, 2)],
    learningOutcomes: [
      "Tell crisp leadership stories with measurable impact",
      "Handle conflict, hiring regrets, and unpopular technical bets",
      "Show team-health awareness with both quantitative and qualitative signals"
    ],
    prerequisites: "1+ year managing engineers, or a strong tech-lead background you can speak to.",
    contentSections: [
      { title: "People and growth", duration: "20 min", type: "practice", completed: false },
      { title: "Prioritization and process", duration: "15 min", type: "practice", completed: false },
      { title: "Conflict and influence", duration: "20 min", type: "practice", completed: false }
    ]
  },
  {
    id: "qb-embedded-engineer",
    tabKey: "question",
    categoryId: "question",
    type: "Question Bank",
    title: "Embedded / Firmware Engineer Question Bank",
    description: "RTOS vs bare metal, OTA, power profiling, watchdogs, DMA, and bring-up questions for firmware and embedded engineers.",
    thumbnail: "https://images.unsplash.com/photo-1553406830-ef2513450d76?auto=format&fit=crop&w=900&q=70",
    sourceName: "InterviewGenieAI",
    role: "Embedded Engineer",
    interviewType: "mixed",
    industry: "hardware",
    skillLevel: "intermediate",
    durationKey: "medium",
    difficulty: "Intermediate",
    duration: "45 min",
    completions: "1.6k",
    rating: 4.6,
    isNew: false,
    isPremium: false,
    isDownloadable: true,
    tags: ["embedded", "firmware", "RTOS", "hardware"],
    questions: [...qs.embedded],
    learningOutcomes: [
      "Choose between RTOS and bare-metal with clear tradeoff reasoning",
      "Explain OTA, power profiling, and watchdog patterns",
      "Tell bring-up and debugging stories with measurable improvement"
    ],
    prerequisites: "Comfort with C/C++ and at least one MCU bring-up.",
    contentSections: [
      { title: "Firmware fundamentals", duration: "15 min", type: "practice", completed: false },
      { title: "Reliability and safety", duration: "15 min", type: "practice", completed: false },
      { title: "Bring-up and process", duration: "15 min", type: "practice", completed: false }
    ]
  },

  /* Cross-cutting question banks (deeper coverage of existing roles) */

  {
    id: "qb-frontend-deep-dive",
    tabKey: "question",
    categoryId: "question",
    type: "Question Bank",
    title: "Frontend Deep Dive: React, Performance, A11y",
    description: "Long-form frontend interview drills covering rendering, hydration, accessibility, and data layer patterns.",
    thumbnail: "https://images.unsplash.com/photo-1581276879432-15e50529f34b?auto=format&fit=crop&w=900&q=70",
    sourceName: "InterviewGenieAI",
    role: "Frontend Developer",
    interviewType: "technical",
    industry: "tech",
    skillLevel: "intermediate",
    durationKey: "long",
    difficulty: "Intermediate",
    duration: "55 min",
    completions: "5.1k",
    rating: 4.7,
    isNew: false,
    isPremium: false,
    isDownloadable: true,
    tags: ["frontend", "React", "performance", "accessibility"],
    questions: [...qs.frontend, ...qs.fullstack.slice(1, 3)],
    learningOutcomes: [
      "Discuss controlled vs uncontrolled, virtualization, and memoization correctly",
      "Walk through A11y from focus management to screen-reader semantics",
      "Debug responsive issues like a senior frontend"
    ],
    prerequisites: "Comfort writing React components and reading browser devtools.",
    contentSections: [
      { title: "React internals", duration: "20 min", type: "practice", completed: false },
      { title: "Performance and rendering", duration: "20 min", type: "practice", completed: false },
      { title: "A11y and UX details", duration: "15 min", type: "practice", completed: false }
    ]
  },
  {
    id: "qb-backend-deep-dive",
    tabKey: "question",
    categoryId: "question",
    type: "Question Bank",
    title: "Backend Deep Dive: APIs, Databases, Distributed Systems",
    description: "Backend prompts on idempotency, caching, queues, transactional outbox, and shard rebalancing.",
    thumbnail: "https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=900&q=70",
    sourceName: "InterviewGenieAI",
    role: "Backend Developer",
    interviewType: "technical",
    industry: "tech",
    skillLevel: "advanced",
    durationKey: "long",
    difficulty: "Advanced",
    duration: "60 min",
    completions: "6.3k",
    rating: 4.8,
    isNew: true,
    isPremium: true,
    isDownloadable: true,
    tags: ["backend", "APIs", "databases", "queues"],
    questions: [...qs.backend, ...qs.cloud.slice(0, 2)],
    learningOutcomes: [
      "Reason about idempotency, retries, and exactly-once semantics",
      "Discuss DB indexes, sharding, and cache invalidation tradeoffs",
      "Design queue-based pipelines with monitoring built in"
    ],
    prerequisites: "Built and operated a real backend service in production or staging.",
    contentSections: [
      { title: "API design and contracts", duration: "20 min", type: "practice", completed: false },
      { title: "Databases and caching", duration: "20 min", type: "practice", completed: false },
      { title: "Queues and consistency", duration: "20 min", type: "practice", completed: false }
    ]
  },
  {
    id: "qb-data-analyst",
    tabKey: "question",
    categoryId: "question",
    type: "Question Bank",
    title: "Data Analyst Question Bank",
    description: "Real interview prompts on SQL, dashboard design, A/B test reading, and stakeholder communication.",
    thumbnail: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=900&q=70",
    sourceName: "InterviewGenieAI",
    role: "Data Analyst",
    interviewType: "mixed",
    industry: "tech",
    skillLevel: "intermediate",
    durationKey: "medium",
    difficulty: "Intermediate",
    duration: "45 min",
    completions: "3.9k",
    rating: 4.6,
    isNew: false,
    isPremium: false,
    isDownloadable: true,
    tags: ["data", "analytics", "SQL", "dashboards"],
    questions: [...qs.data, ...qs.pm.slice(2, 4)],
    learningOutcomes: [
      "Write window-function SQL with confidence",
      "Design a dashboard around a metric tree, not a wishlist",
      "Communicate experiment results in plain language"
    ],
    prerequisites: "Daily SQL usage and basic familiarity with statistics.",
    contentSections: [
      { title: "SQL drills", duration: "20 min", type: "practice", completed: false },
      { title: "Experimentation", duration: "15 min", type: "practice", completed: false },
      { title: "Communication", duration: "10 min", type: "practice", completed: false }
    ]
  },

  /* Real videos (verified YouTube IDs) */

  {
    id: "real-video-amazon-lp-mcmunn",
    tabKey: "video",
    categoryId: "video",
    type: "Video Tutorial",
    title: "Amazon Leadership Principles: 14 questions and answers",
    description: "Richard McMunn walks through Amazon's Leadership Principles with sample answers — useful for any LP-style interview.",
    thumbnail: yt("Ta9tCwcMbXs"),
    youtubeId: "Ta9tCwcMbXs",
    youtubeUrl: "https://www.youtube.com/watch?v=Ta9tCwcMbXs",
    sourceName: "YouTube",
    role: "All Roles",
    interviewType: "behavioral",
    industry: "tech",
    skillLevel: "intermediate",
    durationKey: "medium",
    difficulty: "Intermediate",
    duration: "30 min",
    completions: "9.4k",
    rating: 4.8,
    isNew: false,
    isPremium: false,
    isDownloadable: false,
    tags: ["Amazon", "leadership principles", "behavioral", "STAR"],
    questions: [...qs.engmgr.slice(0, 4), ...qs.pm.slice(0, 2)],
    learningOutcomes: [
      "Speak Amazon LP language: ownership, dive deep, customer obsession",
      "Build LP-tagged stories that work across multiple companies",
      "Deliver tight 90-second answers under interview pressure"
    ],
    prerequisites: "Have 5–8 real career stories ready before you watch.",
    contentSections: [
      { title: "Watch the LP walkthrough", duration: "30 min", type: "video", completed: false },
      { title: "Tag your stories to LPs", duration: "20 min", type: "interactive", completed: false },
      { title: "Deliver out loud", duration: "20 min", type: "practice", completed: false }
    ]
  },
  {
    id: "real-video-amazon-lp-exponent",
    tabKey: "video",
    categoryId: "video",
    type: "Video Tutorial",
    title: "Amazon Leadership Principles for Engineering Managers",
    description: "Exponent's 2024 LP guide aimed at EM and senior IC candidates — what good vs great answers look like.",
    thumbnail: yt("I_uadP83Wwc"),
    youtubeId: "I_uadP83Wwc",
    youtubeUrl: "https://www.youtube.com/watch?v=I_uadP83Wwc",
    sourceName: "YouTube",
    role: "Engineering Manager",
    interviewType: "behavioral",
    industry: "tech",
    skillLevel: "advanced",
    durationKey: "medium",
    difficulty: "Advanced",
    duration: "20 min",
    completions: "5.2k",
    rating: 4.9,
    isNew: true,
    isPremium: false,
    isDownloadable: false,
    tags: ["Amazon", "EM", "leadership", "STAR"],
    questions: [...qs.engmgr.slice(2, 8)],
    learningOutcomes: [
      "Calibrate to the EM bar at Amazon and similar companies",
      "Recognize answer patterns interviewers reward (and the ones they don't)",
      "Plan a focused practice schedule before the loop"
    ],
    prerequisites: "Some management or tech-lead experience to draw stories from.",
    contentSections: [
      { title: "Watch the EM LP walkthrough", duration: "20 min", type: "video", completed: false },
      { title: "Build your story matrix", duration: "30 min", type: "interactive", completed: false }
    ]
  },
  {
    id: "real-video-lp-trap",
    tabKey: "video",
    categoryId: "video",
    type: "Video Tutorial",
    title: "The Leadership Principles TRAP in Amazon Interviews",
    description: "What 95% of candidates get wrong in LP rounds, and what you should do instead. Short, sharp, and recent.",
    thumbnail: yt("XBiyCP-JJ9A"),
    youtubeId: "XBiyCP-JJ9A",
    youtubeUrl: "https://www.youtube.com/watch?v=XBiyCP-JJ9A",
    sourceName: "YouTube",
    role: "All Roles",
    interviewType: "behavioral",
    industry: "tech",
    skillLevel: "intermediate",
    durationKey: "short",
    difficulty: "Intermediate",
    duration: "12 min",
    completions: "3.4k",
    rating: 4.7,
    isNew: true,
    isPremium: false,
    isDownloadable: false,
    tags: ["Amazon", "behavioral", "LP", "tips"],
    questions: [...qs.engmgr.slice(4, 8)],
    learningOutcomes: [
      "Avoid the most common LP-answer mistake",
      "Add concrete metrics to soft stories",
      "Stay calm when the interviewer probes deeper"
    ],
    prerequisites: "None — useful before your first mock LP round.",
    contentSections: [
      { title: "Watch the trap explained", duration: "12 min", type: "video", completed: false },
      { title: "Audit your existing stories", duration: "15 min", type: "interactive", completed: false }
    ]
  },
  {
    id: "real-video-lp-overview",
    tabKey: "video",
    categoryId: "video",
    type: "Video Tutorial",
    title: "Amazon Leadership Principles — overview",
    description: "A clean introduction to all 16 Amazon LPs and what they actually mean inside engineering teams.",
    thumbnail: yt("tnYa75NN0dE"),
    youtubeId: "tnYa75NN0dE",
    youtubeUrl: "https://www.youtube.com/watch?v=tnYa75NN0dE",
    sourceName: "YouTube",
    role: "All Roles",
    interviewType: "behavioral",
    industry: "tech",
    skillLevel: "beginner",
    durationKey: "short",
    difficulty: "Beginner",
    duration: "15 min",
    completions: "6.8k",
    rating: 4.6,
    isNew: false,
    isPremium: false,
    isDownloadable: false,
    tags: ["Amazon", "LP", "overview", "behavioral"],
    questions: [...qs.engmgr.slice(0, 3), ...qs.pm.slice(0, 2)],
    learningOutcomes: [
      "Recognize all 16 LPs and their nuances",
      "Map your past projects to multiple LPs",
      "Walk into the loop with a written story matrix"
    ],
    prerequisites: "None.",
    contentSections: [
      { title: "Watch the LP overview", duration: "15 min", type: "video", completed: false },
      { title: "Tag your projects to LPs", duration: "25 min", type: "interactive", completed: false }
    ]
  },

  /* Learning paths (multi-week structured plans) */

  {
    id: "path-faang-coding-30day",
    tabKey: "path",
    categoryId: "path",
    type: "Learning Path",
    title: "30-Day FAANG Coding Sprint",
    description: "A four-week structured plan covering data structures, algorithms, system design, and behavioral — calibrated for FAANG loops.",
    thumbnail: "https://images.unsplash.com/photo-1517694712202-14dd9538aa97?auto=format&fit=crop&w=900&q=70",
    sourceName: "InterviewGenieAI",
    role: "Software Engineer",
    interviewType: "mixed",
    industry: "tech",
    skillLevel: "advanced",
    durationKey: "extended",
    difficulty: "Advanced",
    duration: "30 days",
    completions: "11.7k",
    rating: 4.9,
    isNew: true,
    isPremium: true,
    isDownloadable: true,
    tags: ["FAANG", "coding", "system design", "30-day"],
    questions: [...qs.fullstack, ...qs.backend.slice(0, 4), ...qs.frontend.slice(0, 3)],
    learningOutcomes: [
      "Master a defined set of patterns rather than random LeetCode",
      "Add a system-design framework you can rely on under pressure",
      "Walk into the loop with rehearsed behavioral answers"
    ],
    prerequisites: "Comfort with at least one language and prior DSA exposure.",
    contentSections: [
      { title: "Week 1 — Data structures and patterns", duration: "7 days", type: "practice", completed: false },
      { title: "Week 2 — Graphs, DP, and edge cases", duration: "7 days", type: "practice", completed: false },
      { title: "Week 3 — System design fundamentals", duration: "7 days", type: "practice", completed: false },
      { title: "Week 4 — Behavioral + mock loops", duration: "9 days", type: "practice", completed: false }
    ]
  },
  {
    id: "path-em-promo-12week",
    tabKey: "path",
    categoryId: "path",
    type: "Learning Path",
    title: "Engineering Manager Promo Path (12 weeks)",
    description: "From senior engineer to first-time EM: shadowing, scope, hiring, conflict, performance, and storytelling.",
    thumbnail: "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=900&q=70",
    sourceName: "InterviewGenieAI",
    role: "Engineering Manager",
    interviewType: "behavioral",
    industry: "tech",
    skillLevel: "advanced",
    durationKey: "extended",
    difficulty: "Advanced",
    duration: "12 weeks",
    completions: "1.9k",
    rating: 4.8,
    isNew: true,
    isPremium: true,
    isDownloadable: true,
    tags: ["EM", "promo", "leadership", "behavioral"],
    questions: [...qs.engmgr],
    learningOutcomes: [
      "Build the story matrix promo committees actually want",
      "Practice 1:1 framing, performance feedback, and hiring rejection conversations",
      "Walk into a panel ready for 'why you, why now' with evidence"
    ],
    prerequisites: "Currently a senior engineer or tech lead with team-facing responsibilities.",
    contentSections: [
      { title: "Weeks 1–3 — Scope and ownership", duration: "3 weeks", type: "practice", completed: false },
      { title: "Weeks 4–6 — People and growth", duration: "3 weeks", type: "practice", completed: false },
      { title: "Weeks 7–9 — Hiring and conflict", duration: "3 weeks", type: "practice", completed: false },
      { title: "Weeks 10–12 — Mock panels", duration: "3 weeks", type: "practice", completed: false }
    ]
  },
  {
    id: "path-pm-2week-bootcamp",
    tabKey: "path",
    categoryId: "path",
    type: "Learning Path",
    title: "2-Week PM Interview Bootcamp",
    description: "Intensive product manager prep: estimation, product sense, execution, and stakeholder behavioral.",
    thumbnail: "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?auto=format&fit=crop&w=900&q=70",
    sourceName: "InterviewGenieAI",
    role: "Product Manager",
    interviewType: "mixed",
    industry: "tech",
    skillLevel: "intermediate",
    durationKey: "long",
    difficulty: "Intermediate",
    duration: "14 days",
    completions: "4.4k",
    rating: 4.7,
    isNew: false,
    isPremium: false,
    isDownloadable: true,
    tags: ["PM", "product manager", "bootcamp"],
    questions: [...qs.pm, ...qs.data.slice(0, 2)],
    learningOutcomes: [
      "Frame product cases with clear users, problems, and metrics",
      "Estimate market size without freezing under pressure",
      "Talk about prioritization tradeoffs convincingly"
    ],
    prerequisites: "PM experience or strong cross-functional background to draw from.",
    contentSections: [
      { title: "Days 1–4 — Product sense", duration: "4 days", type: "practice", completed: false },
      { title: "Days 5–8 — Execution + estimation", duration: "4 days", type: "practice", completed: false },
      { title: "Days 9–12 — Behavioral", duration: "4 days", type: "practice", completed: false },
      { title: "Days 13–14 — Mock panels", duration: "2 days", type: "practice", completed: false }
    ]
  },
  {
    id: "path-data-21day",
    tabKey: "path",
    categoryId: "path",
    type: "Learning Path",
    title: "21-Day Data Scientist / Analyst Path",
    description: "SQL, stats refresh, modeling fundamentals, and stakeholder communication for data-role candidates.",
    thumbnail: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=900&q=70",
    sourceName: "InterviewGenieAI",
    role: "Data Scientist",
    interviewType: "mixed",
    industry: "tech",
    skillLevel: "intermediate",
    durationKey: "extended",
    difficulty: "Intermediate",
    duration: "21 days",
    completions: "3.5k",
    rating: 4.6,
    isNew: true,
    isPremium: false,
    isDownloadable: true,
    tags: ["data", "DS", "analyst", "21-day"],
    questions: [...qs.data, ...qs.ml.slice(0, 4)],
    learningOutcomes: [
      "Solve window-function SQL fluently",
      "Refresh statistics for interviewer-style questions",
      "Communicate model and experiment results to PMs"
    ],
    prerequisites: "Daily SQL or pandas usage.",
    contentSections: [
      { title: "Week 1 — SQL drills", duration: "7 days", type: "practice", completed: false },
      { title: "Week 2 — Stats and ML basics", duration: "7 days", type: "practice", completed: false },
      { title: "Week 3 — Communication + mocks", duration: "7 days", type: "practice", completed: false }
    ]
  },

  /* Docs and guides */

  {
    id: "pdf-mit-star-guide",
    tabKey: "pdf",
    categoryId: "pdf",
    type: "Doc / Guide",
    title: "MIT CAPD: STAR Method Behavioral Interview Guide",
    description: "MIT Career Advising's official STAR-method walkthrough with worksheet and full sample answers.",
    thumbnail: "https://images.unsplash.com/photo-1532619675605-1ede6c2ed2b0?auto=format&fit=crop&w=900&q=70",
    sourceName: "MIT CAPD",
    externalUrl: "https://capd.mit.edu/resources/the-star-method-for-behavioral-interviews/",
    role: "All Roles",
    interviewType: "behavioral",
    industry: "all",
    skillLevel: "beginner",
    durationKey: "short",
    difficulty: "Beginner",
    duration: "15 min read",
    completions: "—",
    rating: 4.8,
    isNew: false,
    isPremium: false,
    isDownloadable: true,
    tags: ["MIT", "STAR", "behavioral", "guide"],
    questions: [...qs.fullstack.slice(3, 4), ...qs.pm.slice(0, 2), ...qs.engmgr.slice(0, 2)],
    learningOutcomes: [
      "Internalize the four-part STAR structure with worked examples",
      "Use the included worksheet to draft 3 stories before your next interview",
      "Calibrate to MIT-grade clarity and concision"
    ],
    prerequisites: "None — best read before your first behavioral mock.",
    contentSections: [
      { title: "Read the MIT STAR guide", duration: "15 min", type: "video", completed: false },
      { title: "Fill out worksheet for 3 stories", duration: "30 min", type: "interactive", completed: false }
    ]
  },
  {
    id: "pdf-bytebytego-system-design",
    tabKey: "pdf",
    categoryId: "pdf",
    type: "Doc / Guide",
    title: "ByteByteGo: Design YouTube — System Design Walkthrough",
    description: "ByteByteGo's article-form walkthrough of the Design YouTube interview problem — clarification, capacity, architecture, tradeoffs.",
    thumbnail: "https://images.unsplash.com/photo-1611162616305-c69b3fa7fbe0?auto=format&fit=crop&w=900&q=70",
    sourceName: "ByteByteGo",
    externalUrl: "https://bytebytego.com/courses/system-design-interview/design-youtube",
    role: "Backend Developer",
    interviewType: "system design",
    industry: "tech",
    skillLevel: "advanced",
    durationKey: "medium",
    difficulty: "Advanced",
    duration: "30 min read",
    completions: "—",
    rating: 4.9,
    isNew: false,
    isPremium: false,
    isDownloadable: true,
    tags: ["system design", "video platform", "scalability", "architecture"],
    questions: [...qs.backend.slice(0, 3), ...qs.cloud.slice(0, 2)],
    learningOutcomes: [
      "Frame a system design problem from clarification to tradeoffs",
      "Estimate storage and bandwidth for a video platform",
      "Talk about CDN, transcoding, and metadata stores fluently"
    ],
    prerequisites: "Basic familiarity with APIs, databases, queues, and CDNs.",
    contentSections: [
      { title: "Read the ByteByteGo walkthrough", duration: "30 min", type: "video", completed: false },
      { title: "Recreate the design from memory", duration: "45 min", type: "practice", completed: false }
    ]
  },
  {
    id: "pdf-exponent-pm-questions",
    tabKey: "pdf",
    categoryId: "pdf",
    type: "Doc / Guide",
    title: "Exponent: Top Product Manager Interview Questions (2026)",
    description: "Exponent's curated list of recent PM questions from FAANG+, AI labs, and fintech — refreshed for 2026.",
    thumbnail: "https://images.unsplash.com/photo-1556155092-490a1ba16284?auto=format&fit=crop&w=900&q=70",
    sourceName: "Exponent",
    externalUrl: "https://www.tryexponent.com/blog/top-product-manager-interview-questions",
    role: "Product Manager",
    interviewType: "mixed",
    industry: "tech",
    skillLevel: "intermediate",
    durationKey: "medium",
    difficulty: "Intermediate",
    duration: "25 min read",
    completions: "—",
    rating: 4.7,
    isNew: true,
    isPremium: false,
    isDownloadable: true,
    tags: ["PM", "product", "FAANG", "2026"],
    questions: [...qs.pm, ...qs.data.slice(0, 2)],
    learningOutcomes: [
      "Get fresh exposure to recent FAANG and AI-lab PM questions",
      "Build a personal stack-rank of which question types you struggle with",
      "Map each example question to a framework you've practiced"
    ],
    prerequisites: "Comfort with PM frameworks like CIRCLES, AARM, or HEART.",
    contentSections: [
      { title: "Read the Exponent question list", duration: "25 min", type: "video", completed: false },
      { title: "Triage: which 5 to drill first?", duration: "20 min", type: "interactive", completed: false }
    ]
  },
  {
    id: "pdf-amazon-lp-igotanoffer",
    tabKey: "pdf",
    categoryId: "pdf",
    type: "Doc / Guide",
    title: "IGotAnOffer: Amazon Leadership Principles Question Bank",
    description: "A long-form guide to Amazon's 16 LPs with example questions, sample answers, and prep strategies.",
    thumbnail: "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=900&q=70",
    sourceName: "IGotAnOffer",
    externalUrl: "https://igotanoffer.com/en/advice/amazon-leadership-principles",
    role: "All Roles",
    interviewType: "behavioral",
    industry: "tech",
    skillLevel: "intermediate",
    durationKey: "long",
    difficulty: "Intermediate",
    duration: "40 min read",
    completions: "—",
    rating: 4.8,
    isNew: false,
    isPremium: false,
    isDownloadable: true,
    tags: ["Amazon", "LP", "behavioral", "guide"],
    questions: [...qs.engmgr, ...qs.pm.slice(0, 3)],
    learningOutcomes: [
      "Cover all 16 LPs with at least one mapped story each",
      "Recognize tricky questions that probe multiple LPs at once",
      "Time-budget your stories so the Situation step doesn't bloat"
    ],
    prerequisites: "None — pairs well with the Amazon LP video resources.",
    contentSections: [
      { title: "Read the LP question bank", duration: "40 min", type: "video", completed: false },
      { title: "Tag your stories to all 16 LPs", duration: "60 min", type: "interactive", completed: false }
    ]
  },

  /* Mock packs (timed full mock interviews) */

  {
    id: "pack-google-l4-loop",
    tabKey: "pack",
    categoryId: "pack",
    type: "Mock Pack",
    title: "Google L4 Software Engineer Mock Loop",
    description: "Phone screen + 4 onsite-style rounds: 2 coding, 1 system design, 1 Googleyness behavioral, with calibrated rubrics.",
    thumbnail: "https://images.unsplash.com/photo-1573164574572-cb89e39749b4?auto=format&fit=crop&w=900&q=70",
    sourceName: "InterviewGenieAI",
    role: "Software Engineer",
    interviewType: "mixed",
    industry: "tech",
    skillLevel: "advanced",
    durationKey: "extended",
    difficulty: "Advanced",
    duration: "5 sessions",
    completions: "7.2k",
    rating: 4.8,
    isNew: true,
    isPremium: true,
    isDownloadable: false,
    tags: ["Google", "L4", "mock", "FAANG"],
    questions: [...qs.fullstack, ...qs.backend.slice(0, 3), ...qs.engmgr.slice(0, 2)],
    learningOutcomes: [
      "Experience pacing pressure of a 45-minute coding round",
      "Get rubric-style feedback after each round",
      "Calibrate against L4 hiring-bar expectations"
    ],
    prerequisites: "Comfortable solving LC medium under 30 minutes.",
    contentSections: [
      { title: "Phone screen — coding", duration: "45 min", type: "practice", completed: false },
      { title: "Onsite 1 — coding", duration: "45 min", type: "practice", completed: false },
      { title: "Onsite 2 — coding", duration: "45 min", type: "practice", completed: false },
      { title: "Onsite 3 — system design", duration: "60 min", type: "practice", completed: false },
      { title: "Onsite 4 — Googleyness", duration: "45 min", type: "practice", completed: false }
    ]
  },
  {
    id: "pack-meta-e5-loop",
    tabKey: "pack",
    categoryId: "pack",
    type: "Mock Pack",
    title: "Meta E5 Engineer Mock Loop",
    description: "Coding + system design + behavioral, calibrated to Meta's E5 bar with focused product-engineering signal.",
    thumbnail: "https://images.unsplash.com/photo-1611162617213-7d7a39e9b1d7?auto=format&fit=crop&w=900&q=70",
    sourceName: "InterviewGenieAI",
    role: "Software Engineer",
    interviewType: "mixed",
    industry: "tech",
    skillLevel: "advanced",
    durationKey: "extended",
    difficulty: "Advanced",
    duration: "5 sessions",
    completions: "5.3k",
    rating: 4.8,
    isNew: true,
    isPremium: true,
    isDownloadable: false,
    tags: ["Meta", "Facebook", "E5", "mock", "FAANG"],
    questions: [...qs.fullstack, ...qs.frontend.slice(0, 4), ...qs.backend.slice(2, 5)],
    learningOutcomes: [
      "Train against Meta's coding bar (LC medium-hard, 2 in 35 min)",
      "Practice Meta-style product/system design",
      "Map your stories to 'Move Fast', 'Be Open', 'Focus on Long-Term Impact'"
    ],
    prerequisites: "Strong coding fluency and at least one product story per Meta value.",
    contentSections: [
      { title: "Coding round 1", duration: "45 min", type: "practice", completed: false },
      { title: "Coding round 2", duration: "45 min", type: "practice", completed: false },
      { title: "Product/system design", duration: "60 min", type: "practice", completed: false },
      { title: "Jedi behavioral", duration: "45 min", type: "practice", completed: false }
    ]
  },
  {
    id: "pack-amazon-sde2-bar-raiser",
    tabKey: "pack",
    categoryId: "pack",
    type: "Mock Pack",
    title: "Amazon SDE II Bar-Raiser Pack",
    description: "Full LP-heavy loop: coding, system design, and a dedicated bar-raiser behavioral round with tough probing follow-ups.",
    thumbnail: "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=900&q=70",
    sourceName: "InterviewGenieAI",
    role: "Software Engineer",
    interviewType: "mixed",
    industry: "tech",
    skillLevel: "advanced",
    durationKey: "extended",
    difficulty: "Advanced",
    duration: "5 sessions",
    completions: "6.0k",
    rating: 4.9,
    isNew: false,
    isPremium: true,
    isDownloadable: false,
    tags: ["Amazon", "SDE II", "bar raiser", "LP"],
    questions: [...qs.fullstack, ...qs.engmgr, ...qs.pm.slice(0, 2)],
    learningOutcomes: [
      "Stand up to bar-raiser-style follow-up questions",
      "Map stories to 16 LPs without rehearsed-sounding answers",
      "Pace coding rounds and avoid the LP 'situation tax'"
    ],
    prerequisites: "5+ practiced career stories, comfort with LC medium.",
    contentSections: [
      { title: "Phone screen — coding", duration: "45 min", type: "practice", completed: false },
      { title: "Coding + LP probing", duration: "45 min", type: "practice", completed: false },
      { title: "System design", duration: "60 min", type: "practice", completed: false },
      { title: "Bar-raiser behavioral", duration: "60 min", type: "practice", completed: false }
    ]
  },
  {
    id: "pack-startup-fullstack-loop",
    tabKey: "pack",
    categoryId: "pack",
    type: "Mock Pack",
    title: "Startup Full-Stack Engineer Loop",
    description: "A scrappier 3-round mock geared toward Series A–C startups: pragmatic coding, end-to-end design, and culture fit.",
    thumbnail: "https://images.unsplash.com/photo-1521737711867-e3b97375f902?auto=format&fit=crop&w=900&q=70",
    sourceName: "InterviewGenieAI",
    role: "Full Stack Developer",
    interviewType: "mixed",
    industry: "tech",
    skillLevel: "intermediate",
    durationKey: "long",
    difficulty: "Intermediate",
    duration: "3 sessions",
    completions: "2.8k",
    rating: 4.7,
    isNew: true,
    isPremium: false,
    isDownloadable: false,
    tags: ["startup", "fullstack", "mock"],
    questions: [...qs.fullstack, ...qs.frontend.slice(0, 2), ...qs.backend.slice(0, 2)],
    learningOutcomes: [
      "Show end-to-end ownership in a small surface area",
      "Talk about scrappy tradeoffs interviewers want to see",
      "Show culture-fit signal without sounding rehearsed"
    ],
    prerequisites: "Built and shipped at least one full-stack project.",
    contentSections: [
      { title: "Pragmatic coding", duration: "45 min", type: "practice", completed: false },
      { title: "End-to-end mini system design", duration: "45 min", type: "practice", completed: false },
      { title: "Founder/EM culture-fit chat", duration: "30 min", type: "practice", completed: false }
    ]
  },

  /* Company kits */

  {
    id: "kit-google-2026",
    tabKey: "company_kit",
    categoryId: "company_kit",
    type: "Company Kit",
    title: "Google Engineering Interview Kit (2026)",
    description: "Coding patterns, system design framework, Googleyness stories, and recent question themes for L3–L5 loops.",
    thumbnail: "https://images.unsplash.com/photo-1573804633927-bfcbcd909acd?auto=format&fit=crop&w=900&q=70",
    sourceName: "InterviewGenieAI",
    role: "Software Engineer",
    interviewType: "mixed",
    industry: "tech",
    skillLevel: "advanced",
    durationKey: "extended",
    difficulty: "Advanced",
    duration: "Self-paced",
    completions: "13.4k",
    rating: 4.9,
    isNew: true,
    isPremium: true,
    isDownloadable: true,
    tags: ["Google", "FAANG", "2026", "kit"],
    questions: [...qs.fullstack, ...qs.backend, ...qs.frontend.slice(0, 3), ...qs.engmgr.slice(0, 2)],
    learningOutcomes: [
      "Practice Google-flavored coding patterns and edge cases",
      "Internalize the 'requirements → API → architecture → tradeoffs' system-design loop",
      "Polish Googleyness stories with measurable impact"
    ],
    prerequisites: "Strong coding fundamentals; system-design exposure helpful.",
    contentSections: [
      { title: "Coding patterns", duration: "Self-paced", type: "practice", completed: false },
      { title: "System design framework", duration: "Self-paced", type: "practice", completed: false },
      { title: "Googleyness stories", duration: "Self-paced", type: "practice", completed: false }
    ]
  },
  {
    id: "kit-amazon-2026",
    tabKey: "company_kit",
    categoryId: "company_kit",
    type: "Company Kit",
    title: "Amazon Interview Kit (LP-Heavy, 2026)",
    description: "16 LPs mapped to common questions, coding pacing tips, and bar-raiser-friendly story templates.",
    thumbnail: "https://images.unsplash.com/photo-1620283085439-39620a1e21c4?auto=format&fit=crop&w=900&q=70",
    sourceName: "InterviewGenieAI",
    role: "Software Engineer",
    interviewType: "mixed",
    industry: "tech",
    skillLevel: "intermediate",
    durationKey: "extended",
    difficulty: "Intermediate",
    duration: "Self-paced",
    completions: "10.1k",
    rating: 4.8,
    isNew: true,
    isPremium: true,
    isDownloadable: true,
    tags: ["Amazon", "LP", "FAANG", "kit"],
    questions: [...qs.engmgr, ...qs.fullstack, ...qs.pm.slice(0, 3)],
    learningOutcomes: [
      "Tag stories to all 16 LPs without overlap",
      "Survive bar-raiser follow-ups without spiraling",
      "Pace coding rounds with LP-flavored behavioral interludes"
    ],
    prerequisites: "5+ rehearsed career stories.",
    contentSections: [
      { title: "16 LPs decoded", duration: "Self-paced", type: "practice", completed: false },
      { title: "Coding + LP combo rounds", duration: "Self-paced", type: "practice", completed: false },
      { title: "Bar-raiser drills", duration: "Self-paced", type: "practice", completed: false }
    ]
  },
  {
    id: "kit-meta-2026",
    tabKey: "company_kit",
    categoryId: "company_kit",
    type: "Company Kit",
    title: "Meta Engineering Kit (2026)",
    description: "Coding, product/system design, and Jedi behavioral framework, calibrated to Meta E4–E6.",
    thumbnail: "https://images.unsplash.com/photo-1611162616305-c69b3fa7fbe0?auto=format&fit=crop&w=900&q=70",
    sourceName: "InterviewGenieAI",
    role: "Software Engineer",
    interviewType: "mixed",
    industry: "tech",
    skillLevel: "advanced",
    durationKey: "extended",
    difficulty: "Advanced",
    duration: "Self-paced",
    completions: "8.7k",
    rating: 4.8,
    isNew: false,
    isPremium: true,
    isDownloadable: true,
    tags: ["Meta", "Facebook", "E4", "E5", "E6", "kit"],
    questions: [...qs.fullstack, ...qs.frontend, ...qs.backend.slice(0, 3)],
    learningOutcomes: [
      "Solve two LC mediums in 35 minutes calmly",
      "Differentiate product design from system design at Meta",
      "Show 'Move Fast' and 'Long-Term Impact' in stories with metrics"
    ],
    prerequisites: "Comfortable with LC medium-hard problems.",
    contentSections: [
      { title: "Coding pacing drills", duration: "Self-paced", type: "practice", completed: false },
      { title: "Product/system design", duration: "Self-paced", type: "practice", completed: false },
      { title: "Jedi behavioral", duration: "Self-paced", type: "practice", completed: false }
    ]
  },
  {
    id: "kit-stripe-2026",
    tabKey: "company_kit",
    categoryId: "company_kit",
    type: "Company Kit",
    title: "Stripe Engineering Kit",
    description: "Pragmatic-bug debugging round, integration-heavy coding, and 'Why Stripe' culture interview prep.",
    thumbnail: "https://images.unsplash.com/photo-1556761175-b413da4baf72?auto=format&fit=crop&w=900&q=70",
    sourceName: "InterviewGenieAI",
    role: "Software Engineer",
    interviewType: "mixed",
    industry: "tech",
    skillLevel: "advanced",
    durationKey: "extended",
    difficulty: "Advanced",
    duration: "Self-paced",
    completions: "3.1k",
    rating: 4.8,
    isNew: true,
    isPremium: true,
    isDownloadable: true,
    tags: ["Stripe", "fintech", "kit"],
    questions: [...qs.backend, ...qs.fullstack.slice(0, 3), ...qs.engmgr.slice(0, 2)],
    learningOutcomes: [
      "Tackle Stripe's bug-fix-style coding round confidently",
      "Reason about idempotency, money, and double-charges precisely",
      "Tell a clear, evidence-rich 'Why Stripe' story"
    ],
    prerequisites: "Familiarity with REST APIs and at least one transactional system.",
    contentSections: [
      { title: "Bug fix coding round", duration: "Self-paced", type: "practice", completed: false },
      { title: "Integration system design", duration: "Self-paced", type: "practice", completed: false },
      { title: "Why Stripe + behavioral", duration: "Self-paced", type: "practice", completed: false }
    ]
  },
  {
    id: "kit-netflix-2026",
    tabKey: "company_kit",
    categoryId: "company_kit",
    type: "Company Kit",
    title: "Netflix Senior Engineer Kit",
    description: "Senior-IC bar: deep system design, distributed-systems edge cases, and Netflix culture-deck behavioral prep.",
    thumbnail: "https://images.unsplash.com/photo-1580130775562-0ef92da028de?auto=format&fit=crop&w=900&q=70",
    sourceName: "InterviewGenieAI",
    role: "Software Engineer",
    interviewType: "mixed",
    industry: "tech",
    skillLevel: "advanced",
    durationKey: "extended",
    difficulty: "Advanced",
    duration: "Self-paced",
    completions: "2.0k",
    rating: 4.9,
    isNew: true,
    isPremium: true,
    isDownloadable: true,
    tags: ["Netflix", "senior", "distributed systems", "kit"],
    questions: [...qs.backend, ...qs.cloud, ...qs.sre.slice(0, 4)],
    learningOutcomes: [
      "Discuss distributed-systems edge cases without hand-waving",
      "Speak Netflix culture deck fluently — context not control, freedom + responsibility",
      "Map career stories to senior-IC scope and judgment signal"
    ],
    prerequisites: "Senior IC experience or strong distributed-systems portfolio.",
    contentSections: [
      { title: "Distributed systems deep dive", duration: "Self-paced", type: "practice", completed: false },
      { title: "Behavioral against the culture deck", duration: "Self-paced", type: "practice", completed: false }
    ]
  },
  {
    id: "kit-openai-2026",
    tabKey: "company_kit",
    categoryId: "company_kit",
    type: "Company Kit",
    title: "OpenAI / AI Lab Engineer Kit",
    description: "ML systems thinking, evaluation rigor, safety-aware product reasoning, and behavioral on ambiguity.",
    thumbnail: "https://images.unsplash.com/photo-1677442136019-21780ecad995?auto=format&fit=crop&w=900&q=70",
    sourceName: "InterviewGenieAI",
    role: "ML Engineer",
    interviewType: "mixed",
    industry: "tech",
    skillLevel: "advanced",
    durationKey: "extended",
    difficulty: "Advanced",
    duration: "Self-paced",
    completions: "1.4k",
    rating: 4.9,
    isNew: true,
    isPremium: true,
    isDownloadable: true,
    tags: ["OpenAI", "AI lab", "ML", "Anthropic", "kit"],
    questions: [...qs.ml, ...qs.security.slice(0, 3), ...qs.pm.slice(0, 2)],
    learningOutcomes: [
      "Think clearly about LLM evaluation, safety, and capability tradeoffs",
      "Reason about product decisions in fast-moving AI products",
      "Tell behavioral stories about ambiguity and high-judgment decisions"
    ],
    prerequisites: "Hands-on experience with at least one production ML or LLM system.",
    contentSections: [
      { title: "ML systems and evals", duration: "Self-paced", type: "practice", completed: false },
      { title: "AI product judgment", duration: "Self-paced", type: "practice", completed: false },
      { title: "Behavioral on ambiguity", duration: "Self-paced", type: "practice", completed: false }
    ]
  },
  {
    id: "kit-apple-2026",
    tabKey: "company_kit",
    categoryId: "company_kit",
    type: "Company Kit",
    title: "Apple Software Engineer Kit",
    description: "Detail-oriented coding, OS- and platform-level system design, and Apple-flavored craft behavioral.",
    thumbnail: "https://images.unsplash.com/photo-1551650975-87deedd944c3?auto=format&fit=crop&w=900&q=70",
    sourceName: "InterviewGenieAI",
    role: "Software Engineer",
    interviewType: "mixed",
    industry: "tech",
    skillLevel: "advanced",
    durationKey: "extended",
    difficulty: "Advanced",
    duration: "Self-paced",
    completions: "4.7k",
    rating: 4.8,
    isNew: false,
    isPremium: true,
    isDownloadable: true,
    tags: ["Apple", "iOS", "macOS", "kit"],
    questions: [...qs.mobile, ...qs.frontend, ...qs.embedded.slice(0, 3)],
    learningOutcomes: [
      "Defend coding choices with quality and craft signal",
      "Handle platform/OS-level system design questions",
      "Tell craft- and quality-focused career stories"
    ],
    prerequisites: "Comfort with at least one Apple-platform language (Swift, Obj-C) is a plus.",
    contentSections: [
      { title: "Detail-oriented coding drills", duration: "Self-paced", type: "practice", completed: false },
      { title: "Platform system design", duration: "Self-paced", type: "practice", completed: false },
      { title: "Craft behavioral", duration: "Self-paced", type: "practice", completed: false }
    ]
  },
  {
    id: "kit-microsoft-2026",
    tabKey: "company_kit",
    categoryId: "company_kit",
    type: "Company Kit",
    title: "Microsoft Software Engineer Kit",
    description: "Azure-flavored coding + system design, Growth Mindset behavioral, and recent Microsoft loop themes.",
    thumbnail: "https://images.unsplash.com/photo-1633419461186-7d40a38105ec?auto=format&fit=crop&w=900&q=70",
    sourceName: "InterviewGenieAI",
    role: "Software Engineer",
    interviewType: "mixed",
    industry: "tech",
    skillLevel: "intermediate",
    durationKey: "extended",
    difficulty: "Intermediate",
    duration: "Self-paced",
    completions: "5.5k",
    rating: 4.7,
    isNew: false,
    isPremium: true,
    isDownloadable: true,
    tags: ["Microsoft", "Azure", "kit"],
    questions: [...qs.fullstack, ...qs.cloud, ...qs.engmgr.slice(0, 2)],
    learningOutcomes: [
      "Solve Microsoft's typical coding problems calmly",
      "Talk through Azure-friendly system designs",
      "Show Growth Mindset signal in behavioral with measurable learning"
    ],
    prerequisites: "Familiarity with one cloud (Azure preferred).",
    contentSections: [
      { title: "Coding patterns", duration: "Self-paced", type: "practice", completed: false },
      { title: "Azure system design", duration: "Self-paced", type: "practice", completed: false },
      { title: "Growth Mindset behavioral", duration: "Self-paced", type: "practice", completed: false }
    ]
  }
];

export const REAL_LIBRARY_CATEGORIES = [
  { id: "all", name: "All" },
  { id: "question", name: "Question Banks" },
  { id: "path", name: "Learning Paths" },
  { id: "video", name: "Video Tutorials" },
  { id: "pdf", name: "Docs & Guides" },
  { id: "pack", name: "Mock Packs" },
  { id: "company_kit", name: "Company Kits" }
];

const normalize = (value) => String(value || "").toLowerCase().trim();

const matchesAny = (value, accepted = []) => {
  if (!accepted?.length) return true;
  return accepted.map(normalize).includes(normalize(value));
};

const textOfResource = (resource) => [
  resource.title,
  resource.description,
  resource.role,
  resource.interviewType,
  resource.industry,
  resource.skillLevel,
  ...(resource.tags || []),
  ...(resource.questions || []).map((q) => `${q.question} ${q.role} ${q.type} ${q.evaluationFocus}`)
].join(" ").toLowerCase();

const tabMatches = (resource, tab) => {
  if (!tab || tab === "all") return true;
  if (tab === "saved") return Boolean(resource.isBookmarked);
  return resource.tabKey === tab || resource.categoryId === tab;
};

export function buildLocalLibraryFeed({ tab = "all", query = "", filters = {} } = {}) {
  const q = normalize(query);
  const activeCategory = filters?.category_id?.[0] || filters?.category?.[0] || "all";

  let items = REAL_LIBRARY_RESOURCES
    .filter((resource) => tabMatches(resource, tab))
    .filter((resource) => activeCategory === "all" || !activeCategory || resource.categoryId === activeCategory)
    .filter((resource) => matchesAny(resource.industry, filters?.industry))
    .filter((resource) => matchesAny(resource.skillLevel, filters?.skillLevel))
    .filter((resource) => matchesAny(resource.durationKey, filters?.duration))
    .filter((resource) => matchesAny(resource.role, filters?.role))
    .filter((resource) => matchesAny(resource.interviewType, filters?.interviewType))
    .filter((resource) => {
      const featureFilters = filters?.features || [];
      if (!featureFilters.length) return true;
      return featureFilters.every((feature) => {
        if (feature === "offline") return Boolean(resource.isDownloadable);
        if (feature === "premium") return Boolean(resource.isPremium);
        if (feature === "interactive") return (resource.contentSections || []).some((s) => s.type === "interactive" || s.type === "practice");
        if (feature === "ai-powered") return true;
        return true;
      });
    })
    .filter((resource) => !q || textOfResource(resource).includes(q));

  const categories = REAL_LIBRARY_CATEGORIES.map((category) => ({
    ...category,
    count: category.id === "all"
      ? REAL_LIBRARY_RESOURCES.length
      : REAL_LIBRARY_RESOURCES.filter((resource) => resource.categoryId === category.id).length
  }));

  const counts = {
    total: items.length,
    tabs: {
      all: REAL_LIBRARY_RESOURCES.length,
      question: REAL_LIBRARY_RESOURCES.filter((r) => r.tabKey === "question").length,
      path: REAL_LIBRARY_RESOURCES.filter((r) => r.tabKey === "path").length,
      video: REAL_LIBRARY_RESOURCES.filter((r) => r.tabKey === "video").length,
      pdf: REAL_LIBRARY_RESOURCES.filter((r) => r.tabKey === "pdf").length,
      pack: REAL_LIBRARY_RESOURCES.filter((r) => r.tabKey === "pack").length,
      company_kit: REAL_LIBRARY_RESOURCES.filter((r) => r.tabKey === "company_kit").length,
      saved: REAL_LIBRARY_RESOURCES.filter((r) => r.isBookmarked).length
    }
  };

  const featured = REAL_LIBRARY_RESOURCES
    .filter((resource) => resource.isNew || resource.isPremium || resource.youtubeId)
    .slice(0, 6);

  const recommendations = REAL_LIBRARY_RESOURCES
    .filter((resource) => ["qb-fullstack-ai-saas", "real-video-star-method-indeed", "path-job-ready-engineer", "real-video-system-design-youtube"].includes(resource.id))
    .slice(0, 4);

  return {
    counts,
    featured,
    items,
    categories,
    active_category: categories.find((category) => category.id === activeCategory) || categories[0],
    continue: {
      ...REAL_LIBRARY_RESOURCES.find((resource) => resource.id === "path-job-ready-engineer"),
      progress: 38
    },
    recommendations
  };
}

export function mergeResourceLists(primary = [], secondary = []) {
  const seen = new Set();
  return [...(primary || []), ...(secondary || [])].filter((resource) => {
    const key = resource?.id || resource?.title;
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}